/* eslint-disable */
import { useMutation, useQuery } from '@tanstack/react-query';
import type { QueryKey, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

import { courseFetcher } from '../Fetcher';
import type { RequestOptions } from '../Fetcher';
const fetcher = courseFetcher;

export type Maybe<T> = T | null;

export type OneOf<T> = T extends unknown[]
  ? T[number]
  : T extends readonly unknown[]
    ? T[number]
    : T;

export type Exact<T extends { [key: string]: unknown }> = {
  [K in keyof T]-?: Exclude<T[K], undefined>;
};

export type Values<T extends Record<string, unknown>> = T extends Record<string, infer U>
  ? U
  : T extends { [key: string]: infer U }
    ? U
    : never;

export const enum ResponseStatus {
  OK = 'OK',
  SERVER_ERROR = 'SERVER_ERROR',
  DUPLICATE = 'DUPLICATE',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  BAD_PARAMETER = 'BAD_PARAMETER',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export type Success<T = unknown> = {
  statusCode: ResponseStatus.OK;
  message: '';
  data: T;
  errorCode: string;
};

export type Error = {
  statusCode: Omit<keyof typeof ResponseStatus, 'OK'>;
  message: string;
  data: Maybe<''>;
  errorCode: string;
};

/**
 * @typeParam T - Response data type
 */
export type ApiResponse<T = unknown> = Success<T>;

export const API_ENDPOINT = {
  CLIENT_V1_VOUCHERS_ENROLLMENT_VERIFICATIONS_CONFIRM:
    '/client/api/v1/vouchers/enrollment-verifications/confirm',
  CLIENT_V1_VOUCHER_ARCHIVE: '/client/api/v1/voucher/archive',
  CLIENT_V1_MY_LISTS: '/client/api/v1/my-lists',
  CLIENT_V1_COURSE_CERTIFICATES_CONFIRM: '/client/api/v1/course-certificates/confirm',
  CLIENT_V1_MY_LISTS_SEQUENCE: '/client/api/v1/my-lists/sequence',
  CLIENT_V1_MY_LISTS_COURSE_SEQUENCE: '/client/api/v1/my-lists/course/sequence',
  CLIENT_V1_MY_LISTS_COURSE: '/client/api/v1/my-lists/course',
  CLIENT_V1_MARKETING_PARTNERS: '/client/api/v1/marketing-partners',
  CLIENT_V1_MARKETING_PARTNER_LINKS: '/client/api/v1/marketing-partner-links',
  CLIENT_V1_COURSE_COURSEID_LIKE: (courseId: string) => `/client/api/v1/course/${courseId}/like`,
  CLIENT_V1_CART: '/client/api/v1/cart',
  CLIENT_V2_VOUCHERS_MY: '/client/api/v2/vouchers/my',
  CLIENT_V2_VOUCHERS_ENROLLMENT_VERIFICATIONS: '/client/api/v2/vouchers/enrollment-verifications',
  CLIENT_V2_MY_LISTS_ID: (id: string) => `/client/api/v2/my-lists/${id}`,
  CLIENT_V1_VOUCHERS_MY_UPCOMING_EVENTS: '/client/api/v1/vouchers/my/upcoming-events',
  CLIENT_V1_VOUCHER_MY_CHALLENGES: '/client/api/v1/voucher/my/challenges',
  CLIENT_V1_USER_STATISTICS: '/client/api/v1/user-statistics',
  CLIENT_V1_ROADMAP_AUTHOR_USERID: (userId: string) => `/client/api/v1/roadmap/author/${userId}`,
  CLIENT_V1_ORDER_SUMMARIES: '/client/api/v1/order/summaries',
  CLIENT_V1_MY_LISTS_ID: (id: string) => `/client/api/v1/my-lists/${id}`,
  CLIENT_V1_MARKETING_PARTNERS_COURSES_SEARCH: '/client/api/v1/marketing-partners/courses/search',
  CLIENT_V1_MARKETING_PARTNER_LINKS_CREATE_PREVIEW:
    '/client/api/v1/marketing-partner-links/create/preview',
  CLIENT_V1_COURSES_ID_SIMPLE: (id: string) => `/client/api/v1/courses/${id}/simple`,
  CLIENT_V1_COURSE_USER_USERID: (userId: string) => `/client/api/v1/course/user/${userId}`,
  CLIENT_V1_COURSE_LIKES: '/client/api/v1/course/likes',
  CLIENT_V1_COURSE_GIFTS_SENT: '/client/api/v1/course-gifts/sent',
  CLIENT_V1_COURSE_GIFTS_RECEIVED: '/client/api/v1/course-gifts/received',
  CLIENT_V1_COURSE_CERTIFICATES: '/client/api/v1/course-certificates',
  CLIENT_V1_COURSE_CERTIFICATES_UNCONFIRMED: '/client/api/v1/course-certificates/unconfirmed',
  CLIENT_V1_COURSE_CERTIFICATES_DETAIL: '/client/api/v1/course-certificates/detail',
  CLIENT_V1_COURSE_CERTIFICATES_CHALLENGE_DETAIL:
    '/client/api/v1/course-certificates/challenge/detail',
  CLIENT_V1_CATEGORY_MAIN: '/client/api/v1/category/main',
  CLIENT_V1_VOUCHERS_ID_FREE: (id: string) => `/client/api/v1/vouchers/${id}/free`,
};

export function useClientApiV1VouchersEnrollmentVerificationsConfirmUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateResponse,
    unknown,
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateResponse,
    unknown,
    ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateVariables =
  ConfirmEnrollmentVerificationRequest;

export type ClientApiV1VouchersEnrollmentVerificationsConfirmUpdateResponse = EmptyOkResponse;

export function useClientApiV1VoucherArchiveUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1VoucherArchiveUpdateResponse,
    unknown,
    ClientApiV1VoucherArchiveUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1VoucherArchiveUpdateResponse,
    unknown,
    ClientApiV1VoucherArchiveUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1VoucherArchiveUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1VoucherArchiveUpdateVariables = ArchiveVoucherV2Request;

export type ClientApiV1VoucherArchiveUpdateResponse = EmptyOkResponse;

export const fetchClientApiV1MyLists = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1MyListsResponse>(url, requestOptions);

export interface ClientApiV1MyListsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1MyListsResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MyListsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MyListsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MyLists(url, requestOptions),
  ...options,
});

export function useClientApiV1MyListsQuery(params: ClientApiV1MyListsQueryParams) {
  const result = useQuery<ClientApiV1MyListsResponse, unknown>(
    getClientApiV1MyListsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsResponse = ApiResponseListFindMyListResponse;

export function useClientApiV1MyListsUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsUpdateResponse,
    unknown,
    ClientApiV1MyListsUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsUpdateResponse,
    unknown,
    ClientApiV1MyListsUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsUpdateVariables = UpdateMyListRequest;

export type ClientApiV1MyListsUpdateResponse = EmptyOkResponse;

export function useClientApiV1MyListsCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCreateResponse,
    unknown,
    ClientApiV1MyListsCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCreateResponse,
    unknown,
    ClientApiV1MyListsCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCreateVariables = CreateMyListRequest;

export type ClientApiV1MyListsCreateResponse = EmptyOkResponse;

export function useClientApiV1CourseCertificatesConfirmUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CourseCertificatesConfirmUpdateResponse,
    unknown,
    ClientApiV1CourseCertificatesConfirmUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CourseCertificatesConfirmUpdateResponse,
    unknown,
    ClientApiV1CourseCertificatesConfirmUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CourseCertificatesConfirmUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCertificatesConfirmUpdateVariables = ConfirmCourseCertificateRequestV2;

export type ClientApiV1CourseCertificatesConfirmUpdateResponse =
  ApiResponseConfirmCourseCertificateResponse;

export function useClientApiV1MyListsSequenceCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsSequenceCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsSequenceCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsSequenceCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsSequenceCreateVariables = UpdateMyListsSequenceRequest;

export type ClientApiV1MyListsSequenceCreateResponse = EmptyOkResponse;

export function useClientApiV1MyListsCourseSequenceCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCourseSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsCourseSequenceCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCourseSequenceCreateResponse,
    unknown,
    ClientApiV1MyListsCourseSequenceCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsCourseSequenceCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCourseSequenceCreateVariables = UpdateMyListCourseSequenceRequest;

export type ClientApiV1MyListsCourseSequenceCreateResponse = EmptyOkResponse;

export const fetchClientApiV1MyListsCourse = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1MyListsCourseResponse>(url, requestOptions);

export interface ClientApiV1MyListsCourseQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MyListsCourseResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MyListsCourseQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MyListsCourseQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MyListsCourse(url, requestOptions),
  ...options,
});

export function useClientApiV1MyListsCourseQuery(params: ClientApiV1MyListsCourseQueryParams) {
  const result = useQuery<ClientApiV1MyListsCourseResponse, unknown>(
    getClientApiV1MyListsCourseQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 강의 기반 강의폴더 조회 */
export type ClientApiV1MyListsCourseQueryString = Partial<{
  courseId: number;
}>;

export type ClientApiV1MyListsCourseResponse = ApiResponseFindMyListByCourseResponse;

export function useClientApiV1MyListsCourseCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCourseCreateResponse,
    unknown,
    ClientApiV1MyListsCourseCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCourseCreateResponse,
    unknown,
    ClientApiV1MyListsCourseCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsCourseCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCourseCreateVariables = AddCourseRequest;

export type ClientApiV1MyListsCourseCreateResponse = EmptyOkResponse;

export function useClientApiV1MyListsCourseDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsCourseDeleteResponse,
    unknown,
    ClientApiV1MyListsCourseDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsCourseDeleteResponse,
    unknown,
    ClientApiV1MyListsCourseDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsCourseDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsCourseDeleteVariables = RemoveCourseRequest;

export type ClientApiV1MyListsCourseDeleteResponse = EmptyOkResponse;

export function useClientApiV1MarketingPartnersCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MarketingPartnersCreateResponse,
    unknown,
    ClientApiV1MarketingPartnersCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MarketingPartnersCreateResponse,
    unknown,
    ClientApiV1MarketingPartnersCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MarketingPartnersCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MarketingPartnersCreateVariables = SignupMarketingPartnerRequest;

export type ClientApiV1MarketingPartnersCreateResponse = ApiResponseSignupMarketingPartnerResponse;

export function useClientApiV1MarketingPartnerLinksCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MarketingPartnerLinksCreateResponse,
    unknown,
    ClientApiV1MarketingPartnerLinksCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MarketingPartnerLinksCreateResponse,
    unknown,
    ClientApiV1MarketingPartnerLinksCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MarketingPartnerLinksCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MarketingPartnerLinksCreateVariables = CreateMarketingPartnerLinkRequest;

export type ClientApiV1MarketingPartnerLinksCreateResponse =
  ApiResponseCreateMarketingPartnerLinkResponse;

export function useClientApiV1CourseCourseIdLikeCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CourseCourseIdLikeCreateResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CourseCourseIdLikeCreateResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CourseCourseIdLikeCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCourseIdLikeCreateResponse = EmptyOkResponse;

export type ClientApiV1CourseCourseIdLikeCreateVariables = unknown;

export function useClientApiV1CourseCourseIdLikeDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CourseCourseIdLikeDeleteResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CourseCourseIdLikeDeleteResponse,
    unknown,
    ClientApiV1CourseCourseIdLikeDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CourseCourseIdLikeDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCourseIdLikeDeleteResponse = EmptyOkResponse;

export type ClientApiV1CourseCourseIdLikeDeleteVariables = unknown;

export const fetchClientApiV1Cart = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1CartResponse>(url, requestOptions);

export interface ClientApiV1CartQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1CartResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CartQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CartQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1Cart(url, requestOptions),
  ...options,
});

export function useClientApiV1CartQuery(params: ClientApiV1CartQueryParams) {
  const result = useQuery<ClientApiV1CartResponse, unknown>(getClientApiV1CartQueryOptions(params));

  return { ...result, data: result.data?.data };
}

/** 클라이언트 장바구니 아이템을 조회한다. */
export type ClientApiV1CartQueryString = Partial<{
  lang: string;
}>;

export type ClientApiV1CartResponse = ApiResponseFindClientCartResponse;

export function useClientApiV1CartCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CartCreateResponse,
    unknown,
    ClientApiV1CartCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CartCreateResponse,
    unknown,
    ClientApiV1CartCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CartCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CartCreateVariables = AddCartsRequest;

export type ClientApiV1CartCreateResponse = ApiResponseAddCartsResponse;

export function useClientApiV1CartDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CartDeleteResponse,
    unknown,
    ClientApiV1CartDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CartDeleteResponse,
    unknown,
    ClientApiV1CartDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CartDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CartDeleteVariables = RemoveCartsRequest;

export type ClientApiV1CartDeleteResponse = EmptyOkResponse;

export const fetchClientApiV2VouchersMy = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV2VouchersMyResponse>(url, requestOptions);

export interface ClientApiV2VouchersMyQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV2VouchersMyResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV2VouchersMyQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2VouchersMyQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2VouchersMy(url, requestOptions),
  ...options,
});

export function useClientApiV2VouchersMyQuery(params: ClientApiV2VouchersMyQueryParams) {
  const result = useQuery<ClientApiV2VouchersMyResponse, unknown>(
    getClientApiV2VouchersMyQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 오프셋기반 페이지네이션 보유한 강의 조회 API */
export type ClientApiV2VouchersMyQueryString = Partial<{
  userId: number;
  searchKeyword: string;
  courseType: CourseType;
  studying: MyVoucherStudyingType;
  isReviewable: boolean;
  isExcludeExpired: boolean;
  isArchived: boolean;
  sort: MyVouchersSortType;
  pageNumber: number;
  pageSize: number;
  lang: string;
}>;

export type ClientApiV2VouchersMyResponse = ApiResponseOffsetPageResponseMyVoucherDto;

export const fetchClientApiV2VouchersEnrollmentVerifications = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2VouchersEnrollmentVerificationsResponse>(url, requestOptions);

export interface ClientApiV2VouchersEnrollmentVerificationsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2VouchersEnrollmentVerificationsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2VouchersEnrollmentVerificationsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2VouchersEnrollmentVerificationsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2VouchersEnrollmentVerifications(url, requestOptions),
  ...options,
});

export function useClientApiV2VouchersEnrollmentVerificationsQuery(
  params: ClientApiV2VouchersEnrollmentVerificationsQueryParams,
) {
  const result = useQuery<ClientApiV2VouchersEnrollmentVerificationsResponse, unknown>(
    getClientApiV2VouchersEnrollmentVerificationsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 수강확인증 목록 조회 API */
export type ClientApiV2VouchersEnrollmentVerificationsQueryString = Partial<{
  requester: Requester;
  userId: number;
  lang: string;
}>;

export type ClientApiV2VouchersEnrollmentVerificationsResponse =
  ApiResponseListGetEnrollmentVerificationResponse;

export const fetchClientApiV2MyListsId = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV2MyListsIdResponse>(url, requestOptions);

export interface ClientApiV2MyListsIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV2MyListsIdResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV2MyListsIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2MyListsIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2MyListsId(url, requestOptions),
  ...options,
});

export function useClientApiV2MyListsIdQuery(params: ClientApiV2MyListsIdQueryParams) {
  const result = useQuery<ClientApiV2MyListsIdResponse, unknown>(
    getClientApiV2MyListsIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 강의폴더에 속한 강의 리스트 조회 */
export type ClientApiV2MyListsIdQueryString = Partial<{
  lang: string;
}>;

export type ClientApiV2MyListsIdResponse = ApiResponseFindMyListCoursesResponse;

export const fetchClientApiV1VouchersMyUpcomingEvents = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1VouchersMyUpcomingEventsResponse>(url, requestOptions);

export interface ClientApiV1VouchersMyUpcomingEventsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1VouchersMyUpcomingEventsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1VouchersMyUpcomingEventsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1VouchersMyUpcomingEventsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1VouchersMyUpcomingEvents(url, requestOptions),
  ...options,
});

export function useClientApiV1VouchersMyUpcomingEventsQuery(
  params: ClientApiV1VouchersMyUpcomingEventsQueryParams,
) {
  const result = useQuery<ClientApiV1VouchersMyUpcomingEventsResponse, unknown>(
    getClientApiV1VouchersMyUpcomingEventsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 내학습 > 도래할 이벤트 조회 API */
export type ClientApiV1VouchersMyUpcomingEventsQueryString = Partial<{
  courseType: CourseType;
  lang: string;
}>;

export type ClientApiV1VouchersMyUpcomingEventsResponse = ApiResponseFindMyUpcomingEventsResponse;

export const fetchClientApiV1VoucherMyChallenges = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1VoucherMyChallengesResponse>(url, requestOptions);

export interface ClientApiV1VoucherMyChallengesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1VoucherMyChallengesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1VoucherMyChallengesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1VoucherMyChallengesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1VoucherMyChallenges(url, requestOptions),
  ...options,
});

export function useClientApiV1VoucherMyChallengesQuery(
  params: ClientApiV1VoucherMyChallengesQueryParams,
) {
  const result = useQuery<ClientApiV1VoucherMyChallengesResponse, unknown>(
    getClientApiV1VoucherMyChallengesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 오프셋기반 페이지네이션 보유한 챌린지 강의 조회 API */
export type ClientApiV1VoucherMyChallengesQueryString = Partial<{
  userId: number;
  searchKeyword: string;
  studying: MyVoucherChallengeStudyingType;
  isReviewable: boolean;
  isArchived: boolean;
  pageSize: number;
  pageNumber: number;
  sort: MyChallengeVouchersOffsetSortType;
  lang: string;
}>;

export type ClientApiV1VoucherMyChallengesResponse =
  ApiResponseOffsetPageResponseMyChallengeVoucherDto;

export const fetchClientApiV1UserStatistics = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1UserStatisticsResponse>(url, requestOptions);

export interface ClientApiV1UserStatisticsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1UserStatisticsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UserStatisticsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UserStatisticsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UserStatistics(url, requestOptions),
  ...options,
});

export function useClientApiV1UserStatisticsQuery(params: ClientApiV1UserStatisticsQueryParams) {
  const result = useQuery<ClientApiV1UserStatisticsResponse, unknown>(
    getClientApiV1UserStatisticsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 사용자 통계 정보 */
export type ClientApiV1UserStatisticsQueryString = Partial<{
  withCache: boolean;
}>;

export type ClientApiV1UserStatisticsResponse = ApiResponseUserStatisticsResponse;

export const fetchClientApiV1RoadmapAuthorUserId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1RoadmapAuthorUserIdResponse>(url, requestOptions);

export interface ClientApiV1RoadmapAuthorUserIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1RoadmapAuthorUserIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1RoadmapAuthorUserIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1RoadmapAuthorUserIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1RoadmapAuthorUserId(url, requestOptions),
  ...options,
});

export function useClientApiV1RoadmapAuthorUserIdQuery(
  params: ClientApiV1RoadmapAuthorUserIdQueryParams,
) {
  const result = useQuery<ClientApiV1RoadmapAuthorUserIdResponse, unknown>(
    getClientApiV1RoadmapAuthorUserIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 주어진 유저가 생성한 로드맵을 조회한다 */
export type ClientApiV1RoadmapAuthorUserIdQueryString = Partial<{
  pageNumber: number;
  pageSize: number;
}>;

export type ClientApiV1RoadmapAuthorUserIdResponse = ApiResponseRoadmapsByAuthorResponse;

export const fetchClientApiV1OrderSummaries = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1OrderSummariesResponse>(url, requestOptions);

export interface ClientApiV1OrderSummariesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1OrderSummariesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1OrderSummariesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1OrderSummariesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1OrderSummaries(url, requestOptions),
  ...options,
});

export function useClientApiV1OrderSummariesQuery(params: ClientApiV1OrderSummariesQueryParams) {
  const result = useQuery<ClientApiV1OrderSummariesResponse, unknown>(
    getClientApiV1OrderSummariesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1OrderSummariesResponse = ApiResponseFindOrderSummaryResponse;

export function useClientApiV1MyListsIdDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MyListsIdDeleteResponse,
    unknown,
    ClientApiV1MyListsIdDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MyListsIdDeleteResponse,
    unknown,
    ClientApiV1MyListsIdDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MyListsIdDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MyListsIdDeleteResponse = EmptyOkResponse;

export type ClientApiV1MyListsIdDeleteVariables = unknown;

export const fetchClientApiV1MarketingPartnersCoursesSearch = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MarketingPartnersCoursesSearchResponse>(url, requestOptions);

export interface ClientApiV1MarketingPartnersCoursesSearchQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MarketingPartnersCoursesSearchResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MarketingPartnersCoursesSearchQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MarketingPartnersCoursesSearchQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MarketingPartnersCoursesSearch(url, requestOptions),
  ...options,
});

export function useClientApiV1MarketingPartnersCoursesSearchQuery(
  params: ClientApiV1MarketingPartnersCoursesSearchQueryParams,
) {
  const result = useQuery<ClientApiV1MarketingPartnersCoursesSearchResponse, unknown>(
    getClientApiV1MarketingPartnersCoursesSearchQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 파트너스 강의 검색 */
export type ClientApiV1MarketingPartnersCoursesSearchQueryString = Partial<{
  userId: number;
  category: string;
  keyword: string;
  sort: SearchPartnerCourseSort;
  pageNumber: number;
  pageSize: number;
  lang: string;
}>;

export type ClientApiV1MarketingPartnersCoursesSearchResponse =
  ApiResponseSearchPartnerCourseResponse;

export const fetchClientApiV1MarketingPartnerLinksCreatePreview = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MarketingPartnerLinksCreatePreviewResponse>(url, requestOptions);

export interface ClientApiV1MarketingPartnerLinksCreatePreviewQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MarketingPartnerLinksCreatePreviewResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MarketingPartnerLinksCreatePreviewQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MarketingPartnerLinksCreatePreviewQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MarketingPartnerLinksCreatePreview(url, requestOptions),
  ...options,
});

export function useClientApiV1MarketingPartnerLinksCreatePreviewQuery(
  params: ClientApiV1MarketingPartnerLinksCreatePreviewQueryParams,
) {
  const result = useQuery<ClientApiV1MarketingPartnerLinksCreatePreviewResponse, unknown>(
    getClientApiV1MarketingPartnerLinksCreatePreviewQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 마케팅 파트너 홍보링크 생성 미리보기를 조회한다 */
export type ClientApiV1MarketingPartnerLinksCreatePreviewQueryString = Partial<{
  courseId: number;
  isAdpickMarketer: boolean;
}>;

export type ClientApiV1MarketingPartnerLinksCreatePreviewResponse =
  ApiResponseGetMarketingPartnerLinkCreatePreviewResponse;

export const fetchClientApiV1CoursesIdSimple = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CoursesIdSimpleResponse>(url, requestOptions);

export interface ClientApiV1CoursesIdSimpleQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CoursesIdSimpleResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CoursesIdSimpleQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CoursesIdSimpleQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CoursesIdSimple(url, requestOptions),
  ...options,
});

export function useClientApiV1CoursesIdSimpleQuery(params: ClientApiV1CoursesIdSimpleQueryParams) {
  const result = useQuery<ClientApiV1CoursesIdSimpleResponse, unknown>(
    getClientApiV1CoursesIdSimpleQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CoursesIdSimpleResponse = ApiResponseCourseSimpleOneResponse;

export const fetchClientApiV1CourseUserUserId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseUserUserIdResponse>(url, requestOptions);

export interface ClientApiV1CourseUserUserIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseUserUserIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseUserUserIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseUserUserIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseUserUserId(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseUserUserIdQuery(
  params: ClientApiV1CourseUserUserIdQueryParams,
) {
  const result = useQuery<ClientApiV1CourseUserUserIdResponse, unknown>(
    getClientApiV1CourseUserUserIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 유저 프로필 강의리스트 조회 */
export type ClientApiV1CourseUserUserIdQueryString = Partial<{
  sort: GetUserProfileCourseSort;
  pageNumber: number;
  pageSize: number;
  lang: string;
}>;

export type ClientApiV1CourseUserUserIdResponse = ApiResponseUserProfileCourseResponse;

export const fetchClientApiV1CourseLikes = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1CourseLikesResponse>(url, requestOptions);

export interface ClientApiV1CourseLikesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1CourseLikesResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseLikesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseLikesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseLikes(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseLikesQuery(params: ClientApiV1CourseLikesQueryParams) {
  const result = useQuery<ClientApiV1CourseLikesResponse, unknown>(
    getClientApiV1CourseLikesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 좋아요한 강의 카드 리스트 조회 */
export type ClientApiV1CourseLikesQueryString = Partial<{
  sort: LikedCourseSort;
  chargeType: LikedCourseChargeType;
  lang: string;
}>;

export type ClientApiV1CourseLikesResponse = ApiResponseCourseByLikesResponse;

export const fetchClientApiV1CourseGiftsSent = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseGiftsSentResponse>(url, requestOptions);

export interface ClientApiV1CourseGiftsSentQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseGiftsSentResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseGiftsSentQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseGiftsSentQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseGiftsSent(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseGiftsSentQuery(params: ClientApiV1CourseGiftsSentQueryParams) {
  const result = useQuery<ClientApiV1CourseGiftsSentResponse, unknown>(
    getClientApiV1CourseGiftsSentQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 보낸 강의 선물 내역 리스트를 조회한다 */
export type ClientApiV1CourseGiftsSentQueryString = Partial<{
  userId: number;
  userAgent: UserAgent;
  pageNumber: number;
  pageSize: number;
}>;

export type ClientApiV1CourseGiftsSentResponse = ApiResponseFindSentCourseGiftResponse;

export const fetchClientApiV1CourseGiftsReceived = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseGiftsReceivedResponse>(url, requestOptions);

export interface ClientApiV1CourseGiftsReceivedQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseGiftsReceivedResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseGiftsReceivedQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseGiftsReceivedQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseGiftsReceived(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseGiftsReceivedQuery(
  params: ClientApiV1CourseGiftsReceivedQueryParams,
) {
  const result = useQuery<ClientApiV1CourseGiftsReceivedResponse, unknown>(
    getClientApiV1CourseGiftsReceivedQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 받은 선물 내역 리스트를 조회한다 */
export type ClientApiV1CourseGiftsReceivedQueryString = Partial<{
  userId: number;
  pageNumber: number;
  pageSize: number;
}>;

export type ClientApiV1CourseGiftsReceivedResponse = ApiResponseFindReceivedCourseGiftResponse;

export const fetchClientApiV1CourseCertificates = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesResponse>(url, requestOptions);

export interface ClientApiV1CourseCertificatesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseCertificatesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseCertificatesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseCertificates(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseCertificatesQuery(
  params: ClientApiV1CourseCertificatesQueryParams,
) {
  const result = useQuery<ClientApiV1CourseCertificatesResponse, unknown>(
    getClientApiV1CourseCertificatesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 사용자의 수료증 목록을 조회한다 */
export type ClientApiV1CourseCertificatesQueryString = Partial<{
  userId: number;
  isArchived: boolean;
  sort: CourseCertificateSortType;
  lang: string;
}>;

export type ClientApiV1CourseCertificatesResponse = ApiResponseListGetCertificatesResponse;

export const fetchClientApiV1CourseCertificatesUnconfirmed = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesUnconfirmedResponse>(url, requestOptions);

export interface ClientApiV1CourseCertificatesUnconfirmedQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesUnconfirmedResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseCertificatesUnconfirmedQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseCertificatesUnconfirmedQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseCertificatesUnconfirmed(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseCertificatesUnconfirmedQuery(
  params: ClientApiV1CourseCertificatesUnconfirmedQueryParams,
) {
  const result = useQuery<ClientApiV1CourseCertificatesUnconfirmedResponse, unknown>(
    getClientApiV1CourseCertificatesUnconfirmedQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1CourseCertificatesUnconfirmedResponse = ApiResponseUnconfirmedResponse;

export const fetchClientApiV1CourseCertificatesDetail = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesDetailResponse>(url, requestOptions);

export interface ClientApiV1CourseCertificatesDetailQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesDetailResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseCertificatesDetailQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseCertificatesDetailQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseCertificatesDetail(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseCertificatesDetailQuery(
  params: ClientApiV1CourseCertificatesDetailQueryParams,
) {
  const result = useQuery<ClientApiV1CourseCertificatesDetailResponse, unknown>(
    getClientApiV1CourseCertificatesDetailQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 수료증 상세 정보를 조회한다 */
export type ClientApiV1CourseCertificatesDetailQueryString = Partial<{
  userId: number;
  voucherId: number;
  courseId: number;
  lang: string;
}>;

export type ClientApiV1CourseCertificatesDetailResponse = ApiResponseGetCertificateDetailResponse;

export const fetchClientApiV1CourseCertificatesChallengeDetail = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCertificatesChallengeDetailResponse>(url, requestOptions);

export interface ClientApiV1CourseCertificatesChallengeDetailQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCertificatesChallengeDetailResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseCertificatesChallengeDetailQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseCertificatesChallengeDetailQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseCertificatesChallengeDetail(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseCertificatesChallengeDetailQuery(
  params: ClientApiV1CourseCertificatesChallengeDetailQueryParams,
) {
  const result = useQuery<ClientApiV1CourseCertificatesChallengeDetailResponse, unknown>(
    getClientApiV1CourseCertificatesChallengeDetailQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 챌린지 수료증 상세 정보를 조회한다 */
export type ClientApiV1CourseCertificatesChallengeDetailQueryString = Partial<{
  userId: number;
  voucherId: number;
  courseId: number;
  lang: string;
}>;

export type ClientApiV1CourseCertificatesChallengeDetailResponse =
  ApiResponseGetCertificateDetailResponse;

export const fetchClientApiV1CategoryMain = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1CategoryMainResponse>(url, requestOptions);

export interface ClientApiV1CategoryMainQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1CategoryMainResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CategoryMainQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CategoryMainQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CategoryMain(url, requestOptions),
  ...options,
});

export function useClientApiV1CategoryMainQuery(params: ClientApiV1CategoryMainQueryParams) {
  const result = useQuery<ClientApiV1CategoryMainResponse, unknown>(
    getClientApiV1CategoryMainQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 부모 카테고리를 조회한다 */
export type ClientApiV1CategoryMainQueryString = Partial<{
  lang: string;
}>;

export type ClientApiV1CategoryMainResponse = ApiResponseFindParentCategoryResponse;

export function useClientApiV1VouchersIdFreeDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1VouchersIdFreeDeleteResponse,
    unknown,
    ClientApiV1VouchersIdFreeDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1VouchersIdFreeDeleteResponse,
    unknown,
    ClientApiV1VouchersIdFreeDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1VouchersIdFreeDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1VouchersIdFreeDeleteResponse = EmptyOkResponse;

export type ClientApiV1VouchersIdFreeDeleteVariables = unknown;

export interface BookRequest {
  thumbnailUrl: string;
  url: string;
  authors: string[];
  translators: string[];
  publisher: string;
  isbn: string;
}

export interface CourseRequest {
  id: number;
}

export interface LinkRequest {
  thumbnailUrl: string;
  url: string;
  iconUrl: string;
}

export interface RoadmapItemRequest {
  title: string;
  description: string;
  type: 'COURSE' | 'BOOK' | 'LINK';
  course?: CourseRequest;
  book?: BookRequest;
  link?: LinkRequest;
}

export interface PublishRoadmapResponse {
  id: number;
}

export interface SaveDraftRoadmapResponse {
  id: number;
}

export interface MissionAttachmentRequest {
  type: 'LINK' | 'IMAGE' | 'FILE';
  value: string;
}

export interface EmptyOkResponse {
  message: string;
  data?: string;
  errorCode?: string;
  statusCode: string;
}

export interface ConfirmEnrollmentVerificationRequest {
  voucherId: number;
}

export interface ArchiveVoucherV2Request {
  id: number;
  isArchive: boolean;
}

export interface EntryEvent {
  code: string;
  entryAt: string;
}

export interface BankAccount {
  code:
    | 'NH'
    | 'KB'
    | 'WOORI'
    | 'SHINHAN'
    | 'IBK'
    | 'HANA'
    | 'GYEONGNAM'
    | 'DAEGU'
    | 'BUSAN'
    | 'SUHYUP'
    | 'POST'
    | 'KDB'
    | 'SC'
    | 'CITI'
    | 'DGB'
    | 'GWANGJU'
    | 'JEJU'
    | 'JEONBUK'
    | 'SAE_MAUL'
    | 'SHINHYEOP'
    | 'K_BANK'
    | 'KAKAO_BANK';
  account: string;
  accountHolder: string;
}

export interface UpdateMyListRequest {
  id: number;
  title: string;
}

export interface UpdateCourseInstructorSettingsRequestDto {
  userId: number;
  isQuestionEmailAllowed: boolean;
  type: 'MAIN' | 'SUB';
}

export interface SyncSurveyChoice {
  id?: number;
  content: string;
}

export interface SyncSurveyQuestion {
  id?: number;
  content: string;
  isRequired: boolean;
  isDefaultQuestion: boolean;
  type: 'SUBJECTIVE' | 'OBJECTIVE' | 'MULTIPLE_OBJECTIVE' | 'FILE';
  choices: SyncSurveyChoice[];
}

export interface UpdateCourseFaqRequestDto {
  id?: number;
  body: string;
  title: string;
}

export interface ConfirmCourseCertificateRequestV2 {
  voucherId: number;
}

export interface ApiResponseConfirmCourseCertificateResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: ConfirmCourseCertificateResponse;
}

export interface ConfirmCourseCertificateResponse {
  now: string;
  nowUTC: string;
}

export interface Benefit {
  type: string;
  iconType?: string;
  title?: string;
}

export interface GetVouchersResponse {
  id: number;
  courseId: number;
  userId: number;
  unitId?: number;
  cartId?: number;
  orderItemId?: number;
  groupId?: number;
  type: string;
  progress: string;
  completeCount: number;
  /**
   * 이전에 사용하던 필드로 하위 호환성을 위해 추가(limitedAt을 전달), 블랙팬서 등 사용하지 않게 되면 제거
   */
  limitedDate?: string;
  createdAt: string;
  startedAt?: string;
  accessedAt?: string;
  completedAt?: string;
  certificatedAt?: string;
  deletedAt?: string;
  isArchive: boolean;
  groupCode?: string;
  hasDownload: boolean;
  hasVideoDownload: boolean;
  completeRuntime: number;
  memo?: string;
  confirmedAt?: string;
  certificateConfirmedAt?: string;
  limitedAt?: string;
  isValid: boolean;
}

export interface ChoiceItem {
  id: number;
  content: string;
}

export interface CommissionRate {
  agreed: number;
  disagreed: number;
}

export interface ExpireSubscriptionVoucherResponse {
  expiredCount: number;
}

export interface UpdateFeaturedChallengeItemRequest {
  courseId: number;
  exposureStartedAtUTC?: string;
  exposureEndedAtUTC?: string;
}

export interface UpdateRoadmapResponse {
  isNewPublish: boolean;
}

export interface CreateRoadmapResponse {
  id: number;
}

export interface CreateOrderBuyerDto {
  name: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
}

export interface CreateOrderCartDto {
  cartId: number;
  courseId: number;
  title: string;
  duration?: number;
  regularPrice: number;
  paymentPrice: number;
  discountId?: number;
  discountType?: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
}

export interface DomesticPayMetadataDto {
  payPgType: 'NAVERPAY' | 'TOSS' | 'TOSS_PAY';
  payMethod:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'PAYCO'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'APPLEPAY'
    | 'VIRTUAL_ACCOUNT'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'BANK_TRANSFER'
    | 'LINKPAY'
    | 'UNKNOWN'
    | 'NONE';
}

export interface Buyer {
  email: string;
  phoneNumber: string;
  name: string;
  countryCode: string;
}

export interface CreateOrderResponse {
  id: number;
  merchantUid: string;
  title: string;
  buyer: Buyer;
  returnUrl: string;
}

export interface CreatePaymentRequestDto {
  cardId: number;
  cardMessage: string;
  payer?: Payer;
}

export interface DomesticPayMetadata {
  payPgType: string;
  payMethod: string;
}

export interface Payer {
  name?: string;
  phone?: string;
}

export interface PaymentRequestCartDto {
  cartId: number;
  title: string;
  regularPrice: number;
  paymentPrice: number;
  discountId?: number;
  discountType: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
}

export interface CreateGlobalOrderCartDto {
  cartId: number;
  courseId: number;
  title: string;
  regularPrice: number;
  paymentPrice: number;
  discountId?: number;
  discountType?: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
}

export interface GlobalPayMetadataDto {
  payPgType: 'PORTONE' | 'TOSS';
  payMethod: 'NONE' | 'PAYPAL';
}

export interface CreateGlobalOrderResponse {
  id: number;
  merchantUid: string;
  title: string;
  buyer: Buyer;
  paymentPrice: number;
  currency: 'KRW' | 'USD' | 'VND' | 'JPY';
  payMetadata: GlobalPayMetadataDto;
  signatureHash: string;
  token?: string;
  returnUrl: string;
}

export interface IssueChallengeEventCouponResponse {
  courseIds: number[];
}

export interface StopMessageResponse {
  code: string;
  stopMessage: string;
}

export interface CreateSectionResponse {
  id: number;
}

export interface SaveRoadmapCourseDto {
  id: number;
  description: string;
}

export interface SaveRoadmapHistoryResponse {
  id: number;
}

export interface RestoreRoadmapHistoryResponse {
  id: number;
}

export interface UploadRoadmapContentVideoResponse {
  url: string;
}

export interface UploadRoadmapContentImageResponse {
  url: string;
}

export interface CreateRoadmapContentTemplateResponse {
  id: number;
}

export interface QuizQuestionChoiceAnswerResponse {
  isAnswer: boolean;
  progress: number;
  explanation?: string;
}

export interface WebhookData {
  paymentKey: string;
  status:
    | 'READY'
    | 'IN_PROGRESS'
    | 'WAITING_FOR_DEPOSIT'
    | 'DONE'
    | 'CANCELED'
    | 'PARTIAL_CANCELED'
    | 'ABORTED'
    | 'EXPIRED';
}

export interface UploadRoadmapThumbnailResponse {
  id: number;
  url: string;
}

export interface UpdateMyListsSequenceRequest {
  ids: number[];
}

export interface UpdateMyListCourseSequenceRequest {
  listId: number;
  courseIds: number[];
}

export interface AddCourseRequest {
  listId: number;
  courseId: number;
}

export interface CreateMyListRequest {
  title: string;
}

export interface CreateMissionLectureResponse {
  id: number;
}

export interface SignupMarketingPartnerRequest {
  name: string;
  email: string;
  phone: string;
  countryCode: string;
  type: 'BUSINESS' | 'PERSONAL';
  activityLink?: string;
}

export interface ApiResponseSignupMarketingPartnerResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: SignupMarketingPartnerResponse;
}

export interface SignupMarketingPartnerResponse {
  id: number;
  commissionRate: CommissionRate;
}

export interface CreateMarketingPartnerLinkRequest {
  courseId: number;
}

export interface ApiResponseCreateMarketingPartnerLinkResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: CreateMarketingPartnerLinkResponse;
}

export interface CreateMarketingPartnerLinkResponse {
  link: string;
}

export interface CreateLiveSessionUnitResponse {
  unitId: number;
}

export interface GetLiveSessionUnitTokenResponse {
  token: string;
  role: 'ADMIN' | 'INSTRUCTOR' | 'STUDENT' | 'STUDENT_VIEWER' | 'RECORDER';
}

export interface ExtendLiveSessionUnitResponse {
  closedAt?: string;
  closedAtUTC: string;
}

export interface CreateLectureResponse {
  id: number;
}

export interface LectureItem {
  fileId: number;
  title: string;
}

export interface CheckKoreanImageHttpResponse {
  hangulTexts?: string;
  hasHangul: boolean;
}

export interface CreateCourseResponse {
  id: number;
}

export interface UploadCourseSurveyFileResponse {
  name: string;
  url: string;
}

export interface SurveyChoiceRequest {
  id: number;
}

export interface SurveyQuestionRequest {
  id: number;
  answer?: string;
  choices: SurveyChoiceRequest[];
}

export interface CourseGift {
  cardId: number;
  cardMessage: string;
  receiveType: 'PHONE' | 'LINK';
  receiver: CreateCourseGiftReceiver;
}

export interface CreateCourseGiftOrderCourseInfo {
  id: number;
  title: string;
  regularPrice: number;
  paymentPrice: number;
  discountId?: number;
  discountType: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
}

export interface CreateCourseGiftReceiver {
  name: string;
  phone?: string;
}

export interface CreateCourseGiftOrderResponse {
  id: number;
  merchantUid: string;
  title: string;
  buyer: Buyer;
  returnUrl: string;
}

export interface CourseVideoMetadata {
  isValidSize: boolean;
  isValidType: boolean;
}

export interface CreateVodUploadUrlResponse {
  uploadUrl: string;
  id: number;
  metadata: VodFileMetadata;
}

export interface VodFileMetadata {
  name: string;
  updatedAt: string;
  type: string;
}

export interface CourseCoverMetadata {
  isValidSize: boolean;
  isValidType: boolean;
  isValidKorean: boolean;
  isValidDimension: boolean;
}

export interface UploadCourseCoverThumbnailMetadata {
  name: string;
  type: string;
  updatedAt: string;
}

export interface UploadCourseCoverThumbnailResponse {
  url: string;
  id: number;
  metadata: UploadCourseCoverThumbnailMetadata;
}

export interface UploadCourseCoverImageResponse {
  url: string;
  fileId: number;
}

export interface RetryCourseCoverAiGenerationResponse {
  retryGenerationHistoryId: number;
}

export interface UploadCourseCoverAiGenerationCompositeMetadata {
  name: string;
  type: string;
  updatedAt: string;
}

export interface UploadCourseCoverAiGenerationCompositeResponse {
  id: number;
  url: string;
  metadata: UploadCourseCoverAiGenerationCompositeMetadata;
}

export interface ContractAgreementRequest {
  contractType: 'PLATFORM' | 'INFLUENCER' | 'OFFLINE' | 'E' | 'EXCLUSIVE' | 'NONEXCLUSIVE';
  b2bSubscriptionAgreement: boolean;
  inflearnMarketingAgreement: boolean;
}

export interface EarlyBirdSaleRequest {
  earlyBirdSaleTerm: 'AGREE' | 'DISAGREE' | 'DELEGATE';
  salePeriodInWeeks?: number;
  saleRate?: number;
}

export interface InflearnMarketingConfirmDto {
  courseId: number;
  isAgreed: boolean;
}

export interface UploadCourseContentVideoResponse {
  url: string;
}

export interface UploadCourseContentImageResponse {
  url: string;
}

export interface CreateCourseContentTemplateResponse {
  id: number;
}

export interface CreateCourseContentHistoryResponse {
  id: number;
}

export interface RestoreCourseContentHistoryResponse {
  id: number;
}

export interface CreateManualCourseContentHistoryResponse {
  id: number;
}

export interface GenerateCourseContentResponse {
  generationHistoryId: number;
}

export interface IssueCouponResponse {
  id: number;
  couponId: number;
  title: string;
  targetType: 'ALL' | 'SELECTED' | 'CATEGORY' | 'SKILL' | 'ROADMAP' | 'COMMON_TAG';
  targetIds: number[];
}

export interface CreateInstructorCouponResponse {
  code: string;
}

export interface UpdateCurrentTimeResponse {
  now: number;
  isCompleted: boolean;
  prevProgress: string;
  progress: string;
  isFirstCompleted: boolean;
}

export interface CommonTagResponse {
  id: number;
  title: string;
  subTitle: string;
  slug: string;
  isCuration: boolean;
}

export interface AddCartsRequest {
  courseIds: number[];
  fbclid?: string;
  eventId?: string;
}

export interface AddCartDto {
  courseId: number;
  cartId: number;
}

export interface AddCartsResponse {
  items: AddCartDto[];
}

export interface ApiResponseAddCartsResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: AddCartsResponse;
}

export interface CreateVoucherItem {
  type:
    | 'LEGACY'
    | 'IS_ADMIN'
    | 'BY_ADMIN'
    | 'IS_INSTRUCTOR'
    | 'BY_INSTRUCTOR'
    | 'FREE'
    | 'ORDER'
    | 'CODE'
    | 'GROUP_ORDER'
    | 'GROUP_SUBSCRIPTION'
    | 'COURSE_GIFT';
  userId: number;
  courseId: number;
  limitedAt?: string;
  orderItemId?: number;
  cartId?: number;
  groupId?: number;
  groupCode?: string;
}

export interface VerifySignUpDiscountResponse {
  isSignUpDiscountDeleted: boolean;
}

export interface UploadPromotionCardImageResponse {
  id: number;
  url: string;
}

export interface Enrolment {
  userId: number;
  courseId: number;
  limitedAt?: string;
  b2bCoursePrice?: number;
  b2bCourseUsdPrice?: number;
}

export interface BatchCreateGroupVoucherResponse {
  voucherId: number;
  userId: number;
  courseId: number;
  regularPrice: number;
  orderItemId: number;
  limitedAt?: string;
  createdAt: string;
}

export interface PromptItem {
  id?: number;
  prompt: string;
  model: string;
}

export interface DetectCourseContentLanguageResponse {
  languageCode: string;
}

export interface ConfirmCourseConfirmationResponse {
  confirmedAt: string;
  confirmedAtUTC: string;
}

export interface UserAgent {
  rawUserAgent: string;
  isInflearnApp: boolean;
  isMobile: boolean;
}

export interface UnitCurrentTime {
  id: number;
  lastCurrentTime: number;
}

export interface BulkUpdateCurrentTimeResponse {
  items: CurrentTimeResponse[];
}

export interface CurrentTimeResponse {
  unitId: number;
  now: number;
  isCompleted: boolean;
  prevProgress: string;
  progress: string;
  isFirstCompleted: boolean;
}

export interface PartnerSkillTagRecommendationItem {
  id: number;
  title: string;
  slug: string;
}

export interface PartnerSkillTagRecommendationResponse {
  items: PartnerSkillTagRecommendationItem[];
}

export interface PartnerCourseRecommendationCategory {
  title: string;
  parentTitle: string;
}

export interface PartnerCourseRecommendationItem {
  url: string;
  title: string;
  thumbnailUrl: string;
  description: string;
  reviewCount: number;
  reviewAverage: number;
  abilities: string[];
  targets: string[];
  instructorName: string;
  level: string;
  categories: PartnerCourseRecommendationCategory[];
  skills: string[];
}

export interface PartnerCourseRecommendationResponse {
  items: PartnerCourseRecommendationItem[];
}

export interface ApiResponseFindParentCategoryResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: FindParentCategoryResponse;
}

export interface FindParentCategoryResponse {
  categories: ParentCategoryDto[];
}

export interface ParentCategoryDto {
  id: number;
  slug: string;
  title: string;
  originalTitle: string;
  icon: string;
}

export interface ChildCategoryDto {
  id: number;
  slug: string;
  title: string;
  originalTitle: string;
}

export interface FindChildCategoryResponse {
  subCategories: ChildCategoryDto[];
}

export interface GetAllLevelsResponse {
  levels: LevelSummaryDto[];
}

export interface LevelSummaryDto {
  title?: string;
  slug: string;
  description?: string;
  code: string;
}

export interface ApiResponseOffsetPageResponseMyVoucherDto {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: OffsetPageResponseMyVoucherDto;
}

export interface MyVoucherChallengeCourseDto {
  id: number;
  hasSurveyAnswer: boolean;
  startedAt?: string;
  endedAt?: string;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface MyVoucherCourseDto {
  id: number;
  slug: string;
  title: string;
  lectureCount: number;
  thumbnailUrl: string;
  instructorId: number;
  instructorName: string;
  offlineCourse?: MyVoucherOfflineCourseDto;
  challengeCourse?: MyVoucherChallengeCourseDto;
  childCategories: string[];
}

export interface MyVoucherDto {
  id: number;
  userId: number;
  unitId: number;
  progress: string;
  completeCount: number;
  limitedAt?: string;
  isArchived: boolean;
  hasCertificate: boolean;
  type:
    | 'LEGACY'
    | 'IS_ADMIN'
    | 'BY_ADMIN'
    | 'IS_INSTRUCTOR'
    | 'BY_INSTRUCTOR'
    | 'FREE'
    | 'ORDER'
    | 'CODE'
    | 'GROUP_ORDER'
    | 'GROUP_SUBSCRIPTION'
    | 'COURSE_GIFT';
  cancelType?: 'B2B' | 'B2C';
  isArchivable: boolean;
  isReviewable: boolean;
  review?: MyVoucherReviewDto;
  course: MyVoucherCourseDto;
  limitedAtUTC?: string;
}

export interface MyVoucherOfflineCourseDto {
  id: number;
  startedAt: string;
  endedAt: string;
  hasSurveyAnswer: boolean;
  canAnswerSurvey: boolean;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface MyVoucherReviewDto {
  id: number;
  content: string;
  rating: number;
  reasons: string[];
}

export interface OffsetPageResponseMyVoucherDto {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: MyVoucherDto[];
}

export interface Requester {
  id: number;
  isAdmin: boolean;
  isBlacklist: boolean;
  isInstructor: boolean;
  isNotAdmin: boolean;
  isNotInstructor: boolean;
}

export interface ApiResponseListGetEnrollmentVerificationResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: GetEnrollmentVerificationResponse[];
}

export interface GetEnrollmentVerificationResponse {
  id: number;
  courseId: number;
  courseSlug: string;
  courseTitle: string;
  courseThumbnailUrl: string;
  isConfirmed: boolean;
  createdAt: string;
  createdAtUTC: string;
}

export interface SearchSkillResponse {
  skills: SkillSummaryDto[];
}

export interface SkillSummaryDto {
  id: number;
  title: string;
  slug: string;
}

export interface BankResponse {
  name: string;
  accountNumber: string;
  limitedAt: string;
  limitedAtUTC: string;
}

export interface ChallengeInfoResponse {
  canAnswerSurvey: boolean;
  hasSurveyAnswer: boolean;
  startedAtUTC?: string;
  endedAtUTC?: string;
  applyStartedAtUTC?: string;
  applyEndedAtUTC?: string;
}

export interface CommonDiscountResponse {
  id?: number;
  title: string;
  type: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
  discountRate: number;
  discountPrice: number;
  discountStrategy: 'PERCENT' | 'PRICE';
  couponId?: number;
}

export interface CourseMetadataLevelResponse {
  code: string;
}

export interface CourseResponse {
  id: number;
  slug: string;
  title: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  studentCount: number;
  totalStar: number;
  reviewCount: number;
  likeCount: number;
  thumbnailUrl: string;
  publishedAt?: string;
  lastUpdatedAt?: string;
  revenueRatio: number;
  providesInstructorAnswer: boolean;
  instructor: OrderPaymentCommonInstructorResponse;
  metadata: OrderPaymentCommonCourseMetadataResponse;
  voucher?: VoucherResponse;
  offline?: OfflineInfoResponse;
  challenge?: ChallengeInfoResponse;
  publishedAtUTC: string;
  lastUpdatedAtUTC: string;
}

export interface MentoringCodeResponse {
  isProvided: boolean;
  isUsed: boolean;
}

export interface OfflineInfoResponse {
  canAnswerSurvey: boolean;
  hasSurveyAnswer: boolean;
  surveyDueDateUTC?: string;
  startedAtUTC?: string;
  endedAtUTC?: string;
  applyStartedAtUTC?: string;
  applyEndedAtUTC?: string;
}

export interface OrderDetailPaymentResponse {
  id: number;
  userId: number;
  createdAt?: string;
  status:
    | 'CANCEL_DELETE'
    | 'MENTEE_CANCEL'
    | 'PARTIAL_CANCEL'
    | 'CANCEL_NOT_ENOUGH_PROGRESS'
    | 'GROUP_PAYMENT_CANCEL'
    | 'PAYMENT_CANCEL'
    | 'ORDER_FORM'
    | 'CONFLICT_CANCEL'
    | 'AUTO_CANCEL'
    | 'PAYMENT_READY'
    | 'TOTAL_CANCEL'
    | 'PAYMENT_FAIL'
    | 'PAYMENT_STOP'
    | 'CODE_ORDER'
    | 'ORDER_CANCEL'
    | 'PAYMENT_COMPLETE'
    | 'PAYMENT_COMPLETE_PROCESSING'
    | 'ADMIN_CANCEL'
    | 'MENTOR_CANCEL'
    | 'DEPOSIT_WAIT'
    | 'WC_COMPLETED'
    | 'MANUAL_DELETE'
    | 'REFUND_REQUEST';
  paymentMethodType?:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'APPLEPAY'
    | 'VIRTUAL_ACCOUNT'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'BANK_TRANSFER'
    | 'PAYCO'
    | 'UNKNOWN'
    | 'NONE'
    | 'LINKPAY'
    | 'LINKPAY_NAVERPAY'
    | 'LINKPAY_KAKAOPAY'
    | 'LINKPAY_TOSSPAY'
    | 'LINKPAY_PAYCO'
    | 'LINKPAY_APPLEPAY'
    | 'LINKPAY_CARD'
    | 'LINKPAY_BANK_TRANSFER'
    | 'FPX'
    | 'SOFORT'
    | 'PAYNOW'
    | 'GRABPAYPH'
    | 'GRABPAYMY'
    | 'BOOST'
    | 'TOUCHNGO'
    | 'KONBINI'
    | 'PAYSHOP'
    | 'BANCONTACT'
    | 'EPS'
    | 'MULTIBANCO'
    | 'PRZELEWY24'
    | 'BLIK'
    | 'IDEAL'
    | 'PLATFORM'
    | 'POLI'
    | 'INSTANT_BANK_TRANSFER'
    | 'UPI';
  regularPrice: number;
  paymentPrice: number;
  discountPrice: number;
  point: number;
  receiptUrl: string;
  isReceiptAvailable: boolean;
  canGetInvoice: boolean;
  isOldDataWithNoReceipt: boolean;
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  bank?: BankResponse;
  currency: 'KRW' | 'USD' | 'VND' | 'JPY';
  timezone: string;
  paidAtUTC?: string;
  isGlobalPayment: boolean;
  refundedPoint: number;
  refundStatus: 'IDLE' | 'PENDING' | 'COMPLETE' | 'REJECT';
  items: OrderItemResponse[];
  requestGift?: RequestGiftResponse;
  createdAtUTC: string;
}

export interface OrderItemResponse {
  cartId: number;
  title: string;
  status:
    | 'AUTO_CANCEL'
    | 'REFUND'
    | 'CANCEL'
    | 'PAID'
    | 'ADMIN_CANCEL'
    | 'CANCEL_MENTOR'
    | 'WC_COMPLETED'
    | 'WAIT'
    | 'MANUAL_DELETE'
    | 'CANCEL_DELETE'
    | 'CANCEL_PROGRESS'
    | 'CANCEL_MENTEE'
    | 'GROUP_PAYMENT_CANCEL'
    | 'ORDER'
    | 'ORDER_CANCEL';
  regularPrice: number;
  paymentPrice: number;
  courseId: number;
  krwRegularPrice: number;
  krwPaymentPrice: number;
  isRefundable: boolean;
  discount?: CommonDiscountResponse;
  mentoringCode: MentoringCodeResponse;
  course?: CourseResponse;
}

export interface OrderPaymentCommonCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parent: ParentCategoryResponse;
}

export interface OrderPaymentCommonCourseMetadataResponse {
  skillTags: OrderPaymentCommonSkillTagResponse[];
  categories: OrderPaymentCommonCourseCategoryResponse[];
  levels: CourseMetadataLevelResponse[];
}

export interface OrderPaymentCommonInstructorResponse {
  userId: number;
  userName: string;
  userSlug?: string;
}

export interface OrderPaymentCommonSkillTagResponse {
  id: number;
  title: string;
  slug: string;
}

export interface ParentCategoryResponse {
  id: number;
  title: string;
  slug: string;
}

export interface PayerResponse {
  name?: string;
  phone?: string;
}

export interface PhotoCardResponse {
  id: number;
  imageUrl: string;
  message: string;
}

export interface RequestGiftResponse {
  type: 'LINK' | 'PHONE';
  paymentLink: string;
  createdAtUTC: string;
  expiredAtUTC: string;
  requester: RequesterResponse;
  payer: PayerResponse;
  photoCard: PhotoCardResponse;
}

export interface RequesterResponse {
  name: string;
  email: string;
  phone: string;
  countryCode: string;
}

export interface VoucherResponse {
  hasDownloadResource: boolean;
  hasDownloadVideo: boolean;
  progress: string;
  createdAt?: string;
  limitedAt?: string;
  createdAtUTC: string;
  limitedAtUTC?: string;
}

export interface GetMyRoadmapResponseV2 {
  id: number;
  status: 'DRAFT' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PRIVATE';
  accessCode?: string;
  title: string;
  thumbnail?: Thumbnail;
  jsonDescription: string;
  htmlDescription: string;
  items: RoadmapItemResponse[];
  latestPublishedHistory?: PublishedHistory;
}

export interface PublishedHistory {
  id: number;
  name: string;
  savedAt: string;
  savedAtUTC: string;
}

export interface RoadmapBookResponse {
  thumbnailUrl: string;
  url: string;
  authors: string[];
  translators: string[];
  publisher: string;
  isbn: string;
}

export interface RoadmapCourseResponse {
  id: number;
  thumbnailUrl: string;
  instructorName: string;
}

export interface RoadmapItemResponse {
  id: number;
  title: string;
  description: string;
  type: 'COURSE' | 'BOOK' | 'LINK';
  course?: RoadmapCourseResponse;
  book?: RoadmapBookResponse;
  link?: RoadmapLinkResponse;
}

export interface RoadmapLinkResponse {
  thumbnailUrl: string;
  url: string;
  iconUrl?: string;
}

export interface Thumbnail {
  id: number;
  url: string;
}

export interface LatestRoadmapHistoryResponse {
  id: number;
  status: 'RESTORE' | 'PUBLISH' | 'DRAFT';
  accessCode?: string;
  title?: string;
  thumbnail: Thumbnail;
  jsonDescription?: string;
  htmlDescription?: string;
  savedAt: string;
  items: RoadmapItemResponse[];
  savedAtUTC: string;
}

export interface ApiResponseFindMyListCoursesResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: FindMyListCoursesResponse;
}

export interface CourseTitle {
  desanitized?: string;
  sanitized?: string;
}

export interface FindMyListCourseInfo {
  id: number;
  thumbnailUrl: string;
  courseTitle?: CourseTitle;
  slug: string;
  courseInstructorName?: string;
  courseInstructorSlug?: string;
  language?: 'KO' | 'EN' | 'JA' | 'VI';
  duration?: number;
  totalStar?: number;
  reviewCount?: number;
  tags: string[];
  lectureCount: number;
  canStudy: boolean;
  voucher?: FindMyListCourseVoucherInfo;
  title: string;
  expireInMonth?: number;
  rating: number;
  instructorName: string;
}

export interface FindMyListCourseVoucherInfo {
  limitedAt?: string;
  completedLectureCount: number;
  progress: number;
  lastUnitId?: number;
  limitedAtUTC?: string;
}

export interface FindMyListCoursesResponse {
  id: number;
  myListTitle?: MyListTitle;
  courses: FindMyListCourseInfo[];
  title: string;
}

export interface MyListTitle {
  value: string;
  desanitized: string;
}

export interface CourseByUserItemResponse {
  id: number;
  title: string;
  slug: string;
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  vodOriginalLanguage:
    | 'KO'
    | 'EN'
    | 'JA'
    | 'VI'
    | 'CN'
    | 'TW'
    | 'HI'
    | 'FR'
    | 'CA'
    | 'ES'
    | 'MX'
    | 'PT';
  type: 'ON' | 'OFF' | 'CHALLENGE';
  thumbnailUrl: string;
}

export interface CourseByUserResponse {
  items: CourseByUserItemResponse[];
}

export interface CourseQuizSummaryResponse {
  totalQuestionCount: number;
  totalCorrectCount: number;
  isAllCompleted: boolean;
  score: number;
  correctRate: number;
}

export interface CourseStepResponse {
  info: boolean;
  curriculum: boolean;
  detail: boolean;
  coverImage: boolean;
  setting: boolean;
  autoMessage: boolean;
  faq: boolean;
  instructor: boolean;
}

export interface ChallengeCourseResponse {
  originalCourseId?: number;
}

export interface CourseCategoryTitleResponse {
  firstCategory: string[];
  secondCategory: string[];
}

export interface CourseInstructorResponse {
  id: number;
  name: string;
  slug?: string;
}

export interface CourseOneByInstructorLevelResponse {
  level: string;
  levelCode: string;
}

export interface CourseOneByInstructorResponse {
  id: number;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  title: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  slug: string;
  price?: number;
  sourceCurrency: 'KRW' | 'USD' | 'VND' | 'JPY';
  duration?: number;
  courseType: 'ONLINE' | 'CLASS' | 'CONFERENCE' | 'CHALLENGE';
  skills: CourseSkillOneResponse[];
  representativeInstructor: CourseInstructorResponse;
  category: CourseCategoryTitleResponse;
  thumbnailUrl: string;
  levels: CourseOneByInstructorLevelResponse[];
  discountedPrice?: number;
  discountStartedAtUTC?: string;
  discountEndedAtUTC?: string;
  instructorCount: number;
  studentCount: number;
  appliedStudentCount: number;
  targetPublishAtUTC?: string;
  submittedAtUTC?: string;
  challengeCourse?: ChallengeCourseResponse;
}

export interface CourseSkillOneResponse {
  id: number;
  title: string;
  slug: string;
  subTitle: string;
}

export interface CurriculumLectureUnitResponse {
  id: number;
  title: string;
  type: 'SECTION' | 'LECTURE' | 'MISSION' | 'DOCUMENT' | 'QUIZ' | 'LIVE_SESSION' | 'LABLUP';
  runtime: number;
  isPreview: boolean;
  hasAttachment: boolean;
  isComplete: boolean;
  body?: string;
  hasBody: boolean;
  hasVideo: boolean;
  submissionStatus?: 'NONE' | 'DRAFT' | 'REJECTED' | 'SUBMITTED' | 'ACCEPTED';
  quizId?: number;
  quizStatus?: QuizStatusResponse;
  isChallengeOnly: boolean;
  liveSession?: CurriculumLiveSessionResponse;
  readingTime?: number;
}

export interface CurriculumLiveSessionResponse {
  status: 'NOT_STARTED' | 'ON_LIVE' | 'ENDED';
  attendanceStatus: 'NONE' | 'ATTENDED' | 'ABSENT';
  recordingOption: 'PROVIDED' | 'NOT_PROVIDED';
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface CurriculumSectionResponse {
  id: number;
  title: string;
  units: CurriculumLectureUnitResponse[];
}

export interface GetCurriculumResponse {
  publishedAt: string;
  lastUpdatedAt: string;
  curriculum: CurriculumSectionResponse[];
  publishedAtUTC?: string;
  lastUpdatedAtUTC: string;
}

export interface QuizStatusResponse {
  status: 'IN_PROGRESS' | 'RETRYING' | 'COMPLETED';
  progress: number;
  correctCount: number;
  questionCount: number;
  retryable: boolean;
  retryCount: number;
  isCompleted: boolean;
  score: number;
}

export interface FaqResponse {
  title: string;
  body: string;
}

export interface GetContentsChallengeCourseInfo {
  originalCourseId?: number;
  originalCourseTitle?: string;
  originalCourseSlug?: string;
  challengePeriod?: Period;
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface GetContentsResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  level: string;
  levelCode: string;
  description: string;
  body: string;
  editorType: 'EDITOR' | 'BUILDER';
  shownCategory: string[];
  abilities: string[];
  targets: string[];
  based: string[];
  faqs: FaqResponse[];
  mainInstructors: MainInstructorWithStatResponse[];
  challengeCourse?: GetContentsChallengeCourseInfo;
  publishedAt?: string;
  lastUpdatedAt: string;
  lastUpdatedAtUTC: string;
  publishedAtUTC?: string;
}

export interface MainInstructorWithStatResponse {
  id: number;
  name: string;
  slug: string;
  introduce: string;
  thumbnail: string;
  courseCount?: number;
  studentCount?: number;
  reviewCount?: number;
  totalStar?: number;
  answerCount?: number;
}

export interface Period {
  startedAt?: string;
  endedAt?: string;
  days?: number;
  valid?: boolean;
  periodDays?: number;
  none?: boolean;
  within?: boolean;
}

export interface BaseCourseChallengeResponse {
  startedAt?: string;
  endedAt?: string;
  applyStartedAt?: string;
  applyEndedAt?: string;
  applyClosedAt?: string;
  capacity: number;
  appliedStudentCount: number;
  displayRemainingApplicantCount?: number;
  hasSurveyAnswer: boolean;
  benefits: ChallengeBenefitResponse[];
  originalCourseId?: number;
  inquiryEmail?: string;
  inquiryLinkUrl?: string;
  startedAtUTC: string;
  endedAtUTC: string;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  applyClosedAtUTC?: string;
}

export interface CartResponse {
  id: number;
}

export interface ChallengeBenefitResponse {
  title: string;
  type: string;
  iconType: string;
}

export interface CourseCategoryResponse {
  main: CourseCategoryTitleWithSlug;
  sub: CourseCategoryTitleWithSlug;
}

export interface CourseCategoryTitleWithSlug {
  title: string;
  slug: string;
}

export interface CourseLevelResponse {
  title: string;
  code: string;
  slug: string;
  isActive: boolean;
}

export interface CoursePaymentDiscountInfoResponse {
  title: string;
  endedAt: string;
  type: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
  discountId?: number;
  endedAtUTC: string;
}

export interface CoursePaymentResponse {
  type:
    | 'ENROLLED'
    | 'PURCHASE_UNAVAILABLE'
    | 'B2B_SUBSCRIPTION'
    | 'B2B_ENROLMENT_PENDING'
    | 'B2B_PURCHASE'
    | 'B2B_PURCHASE_RESTRICTED'
    | 'FREE'
    | 'DISCOUNTED_PURCHASE'
    | 'REGULAR_PRICE_PURCHASE';
  regularPrice: number;
  payPrice: number;
  discountRate: number;
  discount?: CoursePaymentDiscountInfoResponse;
  group?: GroupResponse;
  krwRegularPrice: number;
  krwPaymentPrice: number;
}

export interface CourseVoucherResponseV2 {
  isValid: boolean;
  progress: string;
  completeCount: number;
}

export interface GetChallengeCourseDetailInfoResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  id: number;
  slug?: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  thumbnailUrl: string;
  isNew: boolean;
  isBest: boolean;
  title: string;
  studentCount: number;
  duration?: number;
  providesCertificate: boolean;
  providesInstructorAnswer: boolean;
  providesInquiry: boolean;
  isLiked: boolean;
  likeCount: number;
  description: string;
  unitSummary: UnitSummaryResponse;
  groupEnrolment?: GroupEnrolmentResponse;
  cart?: CartResponse;
  category: CourseCategoryResponse;
  voucher?: CourseVoucherResponseV2;
  paymentInfo: CoursePaymentResponse;
  review: ReviewResponse;
  instructors: CourseInstructorResponse[];
  levels: CourseLevelResponse[];
  skillTags: CourseSkillOneResponse[];
  publishedAt?: string;
  lastUpdatedAt?: string;
  commonTags: CommonTagResponse[];
  isGiftable?: boolean;
  isRequestGiftable: boolean;
  courseType: 'ON' | 'OFF' | 'CHALLENGE';
  challenge: BaseCourseChallengeResponse;
  publishedAtUTC?: string;
  lastUpdatedAtUTC: string;
}

export interface GroupEnrolmentResponse {
  id: number;
}

export interface GroupResponse {
  name: string;
  approvalRequired: boolean;
  hidePrice: boolean;
}

export interface ReviewResponse {
  isShowing: boolean;
  count: number;
  averageStar: number;
}

export interface UnitSummaryResponse {
  lectureUnitCount: number;
  missionUnitCount: number;
  challengeUnitCount: number;
  liveSessionUnitCount: number;
  /**
   * previewVideoLectureCount를 사용해주세요
   */
  previewVideoUnitCount: number;
  previewUnitCount: number;
  previewVideoLectureCount: number;
  runtime: number;
  hasIntroVideo: boolean;
  totalPreviewVideoCount: number;
}

export interface GetChallengePersonalInfoResponse {
  isLiked: boolean;
  voucher?: CourseVoucherResponseV2;
  cart?: CartResponse;
  paymentInfo: CoursePaymentResponse;
  survey: SurveyResponse;
}

export interface SurveyResponse {
  hasSurveyAnswer: boolean;
}

export interface CourseCardCartResponse {
  isInCart: boolean;
  canUseCart: boolean;
}

export interface CourseCardChallengeCourseResponse {
  applyStartedAt?: string;
  applyEndedAt?: string;
  eventStartedAt?: string;
  eventEndedAt?: string;
  challengeStatus?:
    | 'BEFORE_APPLY'
    | 'APPLYING'
    | 'AFTER_APPLY'
    | 'PROCESSING'
    | 'PARTICIPATING'
    | 'COMPLETED';
  capacity: number;
  originalCourseStudentCount: number;
  applicantCount: number;
  canAnswerSurvey: boolean;
  hasSurveyAnswer: boolean;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  eventStartedAtUTC: string;
  eventEndedAtUTC: string;
}

export interface CourseCardCoursePriceResponse {
  isDiscount: boolean;
  isEarlyBirdDiscount: boolean;
  payPrice: number;
  regularPrice: number;
  isFree: boolean;
  discountRate: number;
  discountEndedAt?: string;
  discountId?: number;
  discountTitle?: string;
  discountEndedAtUTC?: string;
}

export interface CourseCardCourseResponse {
  id: number;
  slug: string;
  thumbnailUrl: string;
  title: string;
  description: string;
  reviewCount: number;
  studentCount: number;
  likeCount: number;
  star: number;
  isExclusive: boolean;
  isNew: boolean;
  isUpdated: boolean;
  updatedAt: string;
  publishedAt: string;
  metadata: SearchCourseMetadataResponse;
  abilities: string[];
  courseTags: CourseTagResponse[];
  targets: string[];
  runtimeSecond: number;
  updatedAtUTC: string;
  publishedAtUTC?: string;
}

export interface CourseCardInstructorResponse {
  id: number;
  name: string;
}

export interface CourseCardItem {
  id: number;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  tagTitles: string[];
  isHidePrice: boolean;
  hasMyReaction: boolean;
  hasMentoring: boolean;
  isKdt: boolean;
  offlineCourse?: CourseCardOfflineCourseResponse;
  challengeCourse?: CourseCardChallengeCourseResponse;
  course: CourseCardCourseResponse;
  instructor: CourseCardInstructorResponse;
  listPrice: CourseCardCoursePriceResponse;
  cart: CourseCardCartResponse;
  voucher?: CourseCardVoucherResponse;
  krwRegularPrice: number;
  krwPaymentPrice: number;
}

export interface CourseCardItemCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parent: ParentCategoryResponse;
}

export interface CourseCardOfflineCourseResponse {
  type: 'CONFERENCE' | 'CLASS';
  applyStartedAt: string;
  applyEndedAt: string;
  eventStartedAt: string;
  eventEndedAt: string;
  capacity: number;
  applicantCount: number;
  canAnswerSurvey: boolean;
  hasSurveyAnswer: boolean;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  eventStartedAtUTC: string;
  eventEndedAtUTC: string;
}

export interface CourseCardVoucherResponse {
  completeCount: number;
  lectureCount: number;
  progress: string;
}

export interface CourseTagResponse {
  tag:
    | 'IS_ACTIVE_IN_QUESTION'
    | 'IS_B2B_SUBSCRIPTION'
    | 'HAS_MISSION'
    | 'IS_CHALLENGE_APPLYING'
    | 'IS_CHALLENGE_BEFORE_APPLY'
    | 'IS_CHALLENGE_AFTER_APPLY'
    | 'IS_CHALLENGE_PROCESSING'
    | 'IS_CHALLENGE_COMPLETED'
    | 'IS_CHALLENGE_PARTICIPATING';
}

export interface SearchCourseMetadataResponse {
  level: string;
  levelCode: string;
  /**
   * Use skillTags instead
   */
  skills: string[];
  skillTags: SearchCourseMetadataSkillTagResponse[];
  categories: CourseCardItemCourseCategoryResponse[];
  /**
   * Use categories instead
   */
  parentCategories: string[];
  /**
   * Use categories instead
   */
  childCategories: string[];
}

export interface SearchCourseMetadataSkillTagResponse {
  id: number;
  title: string;
  slug: string;
}

export interface SearchCourseResponse {
  attributionToken: string;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseCardItem[];
}

export interface CategoryInfo {
  id: number;
  slug: string;
  title: string;
}

export interface ChallengeCourseRecommendInstructorInfo {
  id: number;
  name: string;
  slug?: string;
}

export interface ChallengeCourseRecommendResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: RecommendedChallenge[];
  attributionToken: string;
}

export interface RecommendedChallenge {
  id: number;
  isNew: boolean;
  thumbnailUrl?: string;
  title: string;
  slug: string;
  description?: string;
  instructor: ChallengeCourseRecommendInstructorInfo;
  representativeCategory: CategoryInfo;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  applyClosedAtUTC?: string;
  eventStartedAtUTC: string;
  eventEndedAtUTC: string;
  price: number;
  regularPrice: number;
  payPrice: number;
  discountRate?: number;
  isFree: boolean;
  isDiscounting: boolean;
  hasLiveSession: boolean;
  isApplyEnded: boolean;
  challengeStatus:
    | 'BEFORE_APPLY'
    | 'APPLYING'
    | 'AFTER_APPLY'
    | 'PROCESSING'
    | 'PARTICIPATING'
    | 'COMPLETED';
  capacity: number;
  applicantCount: number;
  displayRemainingApplicantCount?: number;
  level: string;
  levelCode: string;
  challengeDurationDays: number;
  applicantRate: number;
}

export interface RecentLearningResponse {
  unitId?: number;
  courseSlug: string;
}

export interface ApiResponseFindMyUpcomingEventsResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: FindMyUpcomingEventsResponse;
}

export interface FindMyUpcomingEventsResponse {
  items: UpcomingEventResponse[];
}

export interface UpcomingEventResponse {
  courseId: number;
  courseTitle: string;
  unitId: number;
  unitTitle: string;
  unitType: 'SECTION' | 'LECTURE' | 'MISSION' | 'DOCUMENT' | 'QUIZ' | 'LIVE_SESSION' | 'LABLUP';
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface FindMyRecentLearningVoucherResponse {
  items: VoucherAndCourse[];
  isRecentLearned: boolean;
}

export interface VoucherAndCourse {
  id: number;
  courseId: number;
  courseSlug: string;
  title?: CourseTitle;
  courseLectureCount: number;
  courseThumbnailUrl: string;
  progress: string;
  completeCount: number;
  unitId: number;
  accessedAtRaw?: string;
  instructorUserId: number;
  instructorUserName: string;
  mainCourseCategory: string;
  courseTitle: string;
  accessedAt: string;
  accessedAtUTC: string;
}

export interface FindMyChallengeVoucherByCursorResponse {
  pageSize: number;
  nextCursor?: string;
  hasNext: boolean;
  items: MyChallengeVoucherDto[];
}

export interface LiveSessionInfo {
  id: number;
  unitId: number;
  title: string;
  startedAt: string;
  isParticipated: boolean;
  status: 'NOT_STARTED' | 'ROOM_OPENED' | 'ROOM_ENDED';
  startedAtUTC: string;
}

export interface MissionInfo {
  id: number;
  unitId: number;
  submissionStatus?: 'DRAFT' | 'REJECTED' | 'SUBMITTED' | 'ACCEPTED';
}

export interface MyChallengeVoucherChallengeCourseDto {
  id: number;
  startedAt: string;
  endedAt: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ENDED';
  missions: MissionInfo[];
  liveSessions: LiveSessionInfo[];
  survey: SurveyInfo;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface MyChallengeVoucherCourseDto {
  id: number;
  slug: string;
  title: string;
  lectureCount: number;
  thumbnailUrl: string;
  instructorId: number;
  instructorName: string;
  challengeCourse: MyChallengeVoucherChallengeCourseDto;
  childCategories: string[];
}

export interface MyChallengeVoucherDto {
  id: number;
  userId: number;
  unitId: number;
  progress: string;
  completeCount: number;
  limitedAt?: string;
  isArchived: boolean;
  hasCertificate: boolean;
  type:
    | 'LEGACY'
    | 'IS_ADMIN'
    | 'BY_ADMIN'
    | 'IS_INSTRUCTOR'
    | 'BY_INSTRUCTOR'
    | 'FREE'
    | 'ORDER'
    | 'CODE'
    | 'GROUP_ORDER'
    | 'GROUP_SUBSCRIPTION'
    | 'COURSE_GIFT';
  cancelType?: 'B2B' | 'B2C';
  isArchivable: boolean;
  isReviewable: boolean;
  review?: MyVoucherReviewDto;
  course: MyChallengeVoucherCourseDto;
  limitedAtUTC?: string;
}

export interface SurveyInfo {
  hasSurveyAnswer: boolean;
}

export interface ApiResponseOffsetPageResponseMyChallengeVoucherDto {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: OffsetPageResponseMyChallengeVoucherDto;
}

export interface OffsetPageResponseMyChallengeVoucherDto {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: MyChallengeVoucherDto[];
}

export interface FindMyVoucherByCursorResponse {
  pageSize: number;
  nextCursor?: string;
  hasNext: boolean;
  items: MyVoucherDto[];
}

export interface CourseChallengeItemResponse {
  id: number;
  userId: number;
  userName: string;
  appliedAt?: string;
  paidAt?: string;
  surveyAnswer?: SurveyAnswerResponse;
  progress: string;
  lectureCount: number;
  missionCount: number;
  liveSessionCount: number;
  completedLectureCount: number;
  completedMissionCount: number;
  completedLiveSessionCount: number;
  isCertificateIssued: boolean;
  isAdmin: boolean;
  appliedAtUTC: string;
  paidAtUTC?: string;
}

export interface CourseChallengeResponse {
  page?: PageCourseChallengeItemResponse;
  instructorCount: number;
  adminCount: number;
  allCount: number;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseChallengeItemResponse[];
}

export interface PageCourseChallengeItemResponse {
  totalElements?: number;
  totalPages?: number;
  size?: number;
  content?: CourseChallengeItemResponse[];
  number?: number;
  sort?: SortObject;
  first?: boolean;
  last?: boolean;
  numberOfElements?: number;
  pageable?: PageableObject;
  empty?: boolean;
}

export interface PageableObject {
  offset?: number;
  sort?: SortObject;
  paged?: boolean;
  pageSize?: number;
  pageNumber?: number;
  unpaged?: boolean;
}

export interface SortObject {
  empty?: boolean;
  sorted?: boolean;
  unsorted?: boolean;
}

export interface SurveyAnswerResponse {
  surveyId: number;
  name: string;
  email: string;
  phone: string;
  answeredAtUTC: string;
  createdAtUTC: string;
}

export interface FindMyVoucherCategoryResponse {
  items: FindVoucherCourseCategoryResult[];
}

export interface FindVoucherCourseCategoryResult {
  id: number;
  title: string;
  count: number;
}

export interface ApiResponseUserStatisticsResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: UserStatisticsResponse;
}

export interface UserStatisticsResponse {
  userCourses: number[];
  userCourseCnt: number;
  userCourseTotalPaymentPrice: number;
  preferCategories: string[];
}

export interface FindSkillResponse {
  skills: SkillSummaryDto[];
}

export interface FindSettlementProductResponse {
  items: SettlementProduct[];
}

export interface SettlementProduct {
  id: number;
  productType: 'ONLINE_COURSE' | 'OFFLINE_COURSE' | 'MENTORING';
  productTitle: string;
}

export interface SectionOneSummaryResponse {
  summaryContents: SummaryDetail[];
}

export interface SummaryContent {
  title: string;
  startSeconds?: number;
  endSeconds?: number;
  content: string;
}

export interface SummaryDetail {
  id: number;
  unitId: number;
  unitTitle: string;
  type: 'VOD_SUBTITLE' | 'UNIT_BODY';
  contents: SummaryContent[];
  hasReaction: boolean;
  reactionType?: 'LIKE' | 'DISLIKE';
  sequence: number;
}

export interface SectionDetail {
  id: number;
  title: string;
  isSummaryVisible: boolean;
}

export interface SectionWithSummaryInfoResponse {
  sections: SectionDetail[];
}

export interface MyRoadmapDto {
  id: number;
  latestHistoryId?: number;
  title: string;
  enrolledCount: number;
  commentCount: number;
  type: 'EDITOR' | 'BUILDER';
  status: 'DRAFT' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PRIVATE';
  createdAt: string;
  updatedAt: string;
  isRemovable: boolean;
  createdAtUTC: string;
  updatedAtUTC: string;
}

export interface OffsetPageResponseMyRoadmapDto {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: MyRoadmapDto[];
}

export interface RoadmapSummaryDto {
  id: number;
  title: string;
  likeCount: number;
  enrollCount: number;
  authorName: string;
  authorThumbnailUrl: string;
  thumbnailUrl: string;
  /**
   * Use authorName instead
   */
  userName: string;
  /**
   * Use thumbnailUrl instead
   */
  coverFileUrl: string;
}

export interface RoadmapSummaryListResponse {
  items: RoadmapSummaryDto[];
}

export interface SearchRoadmapResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: SearchedRoadmapCard[];
}

export interface SearchedRoadmapCard {
  id: number;
  title: string;
  thumbnail: string;
  authorName: string;
  authorThumbnail: string;
  enrollCount: number;
  isEnrolled: boolean;
  roadmapItems: SearchedRoadmapItemDto[];
  skills: SearchedRoadmapSkillDto[];
  coupon?: SearchedRoadmapCouponDto;
}

export interface SearchedRoadmapCouponDiscountDto {
  type: 'PERCENT' | 'PRICE';
  value: number;
}

export interface SearchedRoadmapCouponDto {
  id: number;
  discount: SearchedRoadmapCouponDiscountDto;
  isUnlimited: boolean;
  remainCount: number;
  displayRemainCount: number;
}

export interface SearchedRoadmapItemDto {
  id: number;
  type: 'COURSE' | 'BOOK' | 'LINK';
  title: string;
  thumbnail: string;
  instructorName?: string;
}

export interface SearchedRoadmapSkillDto {
  id: number;
  title: string;
}

export interface EnrolledRoadmapDto {
  id: number;
  title: string;
  courseCount: number;
  completedCourseCount: number;
  thumbnailUrl: string;
  authorName: string;
  completeCount: number;
  lectureCount: number;
  firstCourseId?: number;
  recentCourseId?: number;
}

export interface OffsetPageResponseEnrolledRoadmapDto {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: EnrolledRoadmapDto[];
}

export interface ApiResponseRoadmapsByAuthorResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: RoadmapsByAuthorResponse;
}

export interface RoadmapsByAuthorResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: RoadmapSummaryDto[];
}

export interface FindRoadmapHistoriesResponse {
  id: number;
  name: string;
  savedAt: string;
  type: 'RESTORE' | 'PUBLISH' | 'DRAFT';
  userName: string;
  userId: number;
  origin?: RoadmapHistoryOrigin;
  savedAtUTC: string;
}

export interface RoadmapHistoryOrigin {
  id: number;
  name: string;
  savedAt: string;
  type: 'RESTORE' | 'PUBLISH' | 'DRAFT';
  userName: string;
  userId: number;
  savedAtUTC: string;
}

export interface FindRegistrableRoadmapCourseResponse {
  totalCount: number;
  pageSize: number;
  nextCursor?: string;
  hasNext: boolean;
  items: RegistrableCourseDto[];
}

export interface RegistrableCourseDto {
  id: number;
  title: string;
  slug: string;
  description: string;
  thumbnailUrl: string;
  instructorName: string;
}

export interface FindRoadmapContentTemplateResponse {
  id: number;
  title: string;
  authorType: 'INFLAB' | 'USER';
  htmlDescription: string;
  jsonDescription: string;
  createdAt: string;
  createdAtUTC: string;
}

export interface OffsetPageResponseFindRoadmapContentTemplateResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: FindRoadmapContentTemplateResponse[];
}

export interface CourseReviewInfo {
  id: number;
  rating: number;
  content: string;
  createdAtUTC: string;
  displayCreatedAt: string;
}

export interface DisplayTotalStudyingTime {
  hours: number;
  minutes: number;
}

export interface FirstUnitCompleteInfo {
  id: number;
  courseId: number;
  courseTitle: string;
  courseThumbnail: string;
  representativeInstructorName: string;
  completedAtUTC: string;
  displayFirstUnitCompleteDate: string;
}

export interface GetRecap2025CourseInfo {
  id: number;
  title: string;
  thumbnail: string;
  representativeInstructorName: string;
}

export interface GetRecap2025InstructorInfo {
  id: number;
  name: string;
  thumbnail: string;
}

export interface GetRecap2025Response {
  rank: 'MASTER' | 'SCHOLAR' | 'SKILL_BUILDER' | 'LEARNER' | 'STARTER';
  totalStudyingTimeSeconds: number;
  totalStudyingCourseCount: number;
  studyingCourses: GetRecap2025CourseInfo[];
  totalCompleteCourseCount: number;
  completeCourses: GetRecap2025CourseInfo[];
  totalSkillTagCount: number;
  skillTags: SkillTagInfo[];
  firstUnitComplete: FirstUnitCompleteInfo;
  totalInstructorCount: number;
  instructors: GetRecap2025InstructorInfo[];
  maxCompleteCourseInstructor: GetRecap2025InstructorInfo;
  maxStudyingTimeCompletedAt?: string;
  maxStudyingTimeMonths: number[];
  maxStudyingTimeDayOfWeek:
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY';
  maxStudyingTimeTimeOfDay:
    | 'EARLY_MORNING'
    | 'MORNING'
    | 'AFTERNOON'
    | 'EVENING'
    | 'NIGHT'
    | 'DAWN';
  maxContinuousStudyDay: number;
  totalCourseReviewCount: number;
  courseReviews: CourseReviewInfo[];
  totalQuestionCount: number;
  questions: QuestionInfo[];
  totalNoteCount: number;
  displayTotalStudyingTime: DisplayTotalStudyingTime;
  maxStudyingTimeCompletedAtUTC: string;
  displayMaxStudyingTimeCompletedAtDate: string;
  displayMaxStudyingTimeMonths: string;
  displayMaxStudyingTimeTimeOfDayTime: string;
  displayMaxStudyingTimeTimeOfDayTimeSlot: string;
  displayMaxStudyingTimeDayOfWeek: string;
}

export interface QuestionInfo {
  id: number;
  title: string;
  createdAtUTC: string;
  displayCreatedAt: string;
}

export interface SkillTagInfo {
  id: number;
  title: string;
}

export interface CheckRecap2025ExistsResponse {
  hasRecap: boolean;
}

export interface QuizFindOneResponse {
  id: number;
  quizUserStatus: 'IN_PROGRESS' | 'RETRYING' | 'COMPLETED';
  questions: QuizQuestionOneResponse[];
}

export interface QuizQuestionChoiceOneResponse {
  id: number;
  content: string;
}

export interface QuizQuestionOneResponse {
  id: number;
  title: string;
  choices: QuizQuestionChoiceOneResponse[];
  retryCount: number;
  quizQuestionUserStatus: 'PASS' | 'FAIL' | 'NOT_ATTEMPTED';
  reactionType?: 'LIKE' | 'DISLIKE';
  hasReaction: boolean;
}

export interface QuizLectureUserStatsResponse {
  quizId: number;
  correctCount: number;
  questionCount: number;
  retryLeftCount: number;
  retryable: boolean;
}

export interface QuizFindOneStatusResponse {
  status: 'NOT_STARTED' | 'NO_PERMISSION' | 'IN_PROGRESS' | 'COMPLETED';
}

export interface QuizFindOneQuestion {
  questionId: number;
  questionUserId: number;
  title: string;
  explanation?: string;
  lastChoiceId: number;
  status: 'PASS' | 'FAIL' | 'NOT_ATTEMPTED';
  choices: QuizFindOneResultChoice[];
  reactionType?: 'LIKE' | 'DISLIKE';
  hasReaction: boolean;
}

export interface QuizFindOneResultChoice {
  sequence: number;
  content: string;
  choiceId: number;
  isAnswer?: boolean;
}

export interface QuizFindOneResultResponse {
  id: number;
  questions: QuizFindOneQuestion[];
  retryLeftCount: number;
  retryable: boolean;
}

export interface EntryMarketingPartnerResponse {
  id: number;
  userId: number;
  name: string;
  type: 'BUSINESS' | 'PERSONAL';
}

export interface EntryPromotionLinkResponse {
  id: number;
}

export interface InstructorPromotionLinkEntryCourseResponse {
  id: number;
  title: string;
  slug: string;
}

export interface InstructorPromotionLinkEntryEventsDetailResponseItem {
  entryAt: string;
  code: string;
  expiredAt?: string;
  course?: InstructorPromotionLinkEntryCourseResponse;
}

export interface MarketingPromotionLinkEntryCourseResponse {
  id: number;
  title: string;
  slug: string;
  isInflearnMarketingAgreed: boolean;
}

export interface MarketingPromotionLinkEntryEventDetailResponseItem {
  entryAt: string;
  code: string;
  expiredAt?: string;
  course?: MarketingPromotionLinkEntryCourseResponse;
  promotionLink?: EntryPromotionLinkResponse;
  marketingPartner?: EntryMarketingPartnerResponse;
}

export interface PromotionLinkEntryEventsDetailResponse {
  instructorPromotionLinkEntryEvents: InstructorPromotionLinkEntryEventsDetailResponseItem[];
  marketingPromotionLinkEntryEvents: MarketingPromotionLinkEntryEventDetailResponseItem[];
}

export interface SearchPromotionCardsResponse {
  items: SearchPromotionItem[];
}

export interface SearchPromotionCourseItem {
  tagTitle: string;
  course: CourseCardItem;
}

export interface SearchPromotionItem {
  id: number;
  type: 'COURSE' | 'URL';
  course?: SearchPromotionCourseItem;
  url?: SearchPromotionUrlItem;
  title: string;
  sequence: number;
}

export interface SearchPromotionUrlItem {
  image: string;
  altText: string;
  url: string;
}

export interface CourseBanner {
  id: number;
  courseId: number;
  title: string;
  description: string;
  slug: string;
  thumbnailUrl: string;
  discount?: FindPromotionBannerDiscountInfo;
  tagTitle: string;
  endedAtUTC?: string;
  startedAtUTC: string;
}

export interface FindPromotionBannerDiscountInfo {
  isEarlyBirdDiscount: boolean;
  title: string;
  rate: number;
  endedAtUTC: string;
}

export interface FindPromotionBannerListResponse {
  items: PromotionBannerItem[];
}

export interface PromotionBannerItem {
  type: 'COURSE' | 'URL';
  course?: CourseBanner;
  url?: UrlBanner;
}

export interface UrlBanner {
  id: number;
  bannerTitle?: string;
  url: string;
  altText: string;
  bannerImageId: number;
  bannerImageUrl: string;
  bannerMobileImageId: number;
  bannerMobileImageUrl: string;
  isDDayActive: boolean;
  endedAtUTC?: string;
  startedAtUTC: string;
}

export interface GetMyPointsResponse {
  point: number;
}

export interface GetPaymentRequestStatusResponse {
  status:
    | 'CANCEL_DELETE'
    | 'MENTEE_CANCEL'
    | 'PARTIAL_CANCEL'
    | 'CANCEL_NOT_ENOUGH_PROGRESS'
    | 'GROUP_PAYMENT_CANCEL'
    | 'PAYMENT_CANCEL'
    | 'ORDER_FORM'
    | 'CONFLICT_CANCEL'
    | 'AUTO_CANCEL'
    | 'PAYMENT_READY'
    | 'TOTAL_CANCEL'
    | 'PAYMENT_FAIL'
    | 'PAYMENT_STOP'
    | 'CODE_ORDER'
    | 'ORDER_CANCEL'
    | 'PAYMENT_COMPLETE'
    | 'PAYMENT_COMPLETE_PROCESSING'
    | 'ADMIN_CANCEL'
    | 'MENTOR_CANCEL'
    | 'DEPOSIT_WAIT'
    | 'WC_COMPLETED'
    | 'MANUAL_DELETE'
    | 'REFUND_REQUEST';
  requesterName: string;
  createdAtUTC: string;
  paymentPrice: number;
  orderId: number;
}

export interface GetPaymentRequestCouponEligibilityResponse {
  requesterName: string;
  discountPercent: number;
  expireOffsetDay: number;
}

export interface GetValidPaymentRequestResponse {
  paymentUrl: string;
  expiredAtUTC: string;
  createdAtUTC: string;
  requesterName: string;
  photoCard: PhotoCardResponse;
}

export interface GetPaymentRequestPhotoCardsResponse {
  items: PaymentRequestPhotoCardResponse[];
}

export interface PaymentRequestPhotoCardResponse {
  id: number;
  title: string;
  url: string;
  isDefault: boolean;
}

export interface ApiResponseFindOrderSummaryResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: FindOrderSummaryResponse;
}

export interface FindOrderSummaryResponse {
  items: OrderSummaryDto[];
  statistics: UserPurchaseStatisticsResult;
}

export interface OrderSummaryDto {
  id: number;
  title: string;
  createdAt: string;
  status:
    | 'CANCEL_DELETE'
    | 'MENTEE_CANCEL'
    | 'PARTIAL_CANCEL'
    | 'CANCEL_NOT_ENOUGH_PROGRESS'
    | 'GROUP_PAYMENT_CANCEL'
    | 'PAYMENT_CANCEL'
    | 'ORDER_FORM'
    | 'CONFLICT_CANCEL'
    | 'AUTO_CANCEL'
    | 'PAYMENT_READY'
    | 'TOTAL_CANCEL'
    | 'PAYMENT_FAIL'
    | 'PAYMENT_STOP'
    | 'CODE_ORDER'
    | 'ORDER_CANCEL'
    | 'PAYMENT_COMPLETE'
    | 'PAYMENT_COMPLETE_PROCESSING'
    | 'ADMIN_CANCEL'
    | 'MENTOR_CANCEL'
    | 'DEPOSIT_WAIT'
    | 'WC_COMPLETED'
    | 'MANUAL_DELETE'
    | 'REFUND_REQUEST';
  orderMode:
    | 'NORMAL'
    | 'B2B_CODE'
    | 'B2C_CODE'
    | 'CODE'
    | 'MENTOR'
    | 'MENTORING'
    | 'COURSE_GIFT'
    | 'CLIP';
  regularPrice: number;
  paymentPrice: number;
  itemsCount: number;
  receiptUrl: string;
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  paymentMethodType?:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'APPLEPAY'
    | 'VIRTUAL_ACCOUNT'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'BANK_TRANSFER'
    | 'PAYCO'
    | 'UNKNOWN'
    | 'NONE'
    | 'LINKPAY'
    | 'LINKPAY_NAVERPAY'
    | 'LINKPAY_KAKAOPAY'
    | 'LINKPAY_TOSSPAY'
    | 'LINKPAY_PAYCO'
    | 'LINKPAY_APPLEPAY'
    | 'LINKPAY_CARD'
    | 'LINKPAY_BANK_TRANSFER'
    | 'FPX'
    | 'SOFORT'
    | 'PAYNOW'
    | 'GRABPAYPH'
    | 'GRABPAYMY'
    | 'BOOST'
    | 'TOUCHNGO'
    | 'KONBINI'
    | 'PAYSHOP'
    | 'BANCONTACT'
    | 'EPS'
    | 'MULTIBANCO'
    | 'PRZELEWY24'
    | 'BLIK'
    | 'IDEAL'
    | 'PLATFORM'
    | 'POLI'
    | 'INSTANT_BANK_TRANSFER'
    | 'UPI';
  /**
   * use isReceiptAvailable
   */
  canGetReceipt: boolean;
  isReceiptAvailable: boolean;
  canGetInvoice: boolean;
  isOldDataWithNoReceipt: boolean;
  currency: 'KRW' | 'USD' | 'VND' | 'JPY';
  timezone: string;
  createdAtUTC: string;
  isPaymentRequest: boolean;
}

export interface UserPurchaseStatisticsResult {
  totalCount: number;
  totalOrderAmount: number;
  totalPaymentAmount: number;
  totalDiscountAmount: number;
}

export interface OfflineCourseStepResponse {
  info: boolean;
  detail: boolean;
  curriculum: boolean;
  survey: boolean;
  coverImage: boolean;
  setting: boolean;
  faq: boolean;
  instructor: boolean;
}

export interface FindLatestDraftResponse {
  id: number;
  savedAt: string;
  savedAtUTC: string;
}

export interface ApiResponseFindMyListByCourseResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: FindMyListByCourseResponse;
}

export interface FindMyListByCourseResponse {
  items: ItemResponse[];
}

export interface ItemResponse {
  id: number;
  title: string;
  hasCourse: boolean;
}

export interface ApiResponseListFindMyListResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: FindMyListResponse[];
}

export interface FindMyListResponse {
  id: number;
  title: string;
  courseCount: number;
}

export interface MainInstructor {
  id: number;
  name: string;
  slug?: string;
  thumbnailUrl: string;
}

export interface MissionLectureAttachmentResponse {
  type: 'LINK' | 'IMAGE' | 'FILE';
  url: string;
  fileId?: number;
  name?: string;
  value: string;
}

export interface MissionLectureAuthorOneResponse {
  id: number;
  name: string;
  slug?: string;
  thumbnailUrl: string;
}

export interface MissionLectureOneResponse {
  id: number;
  title: string;
  missionId: number;
  body: string;
  attachments: MissionLectureAttachmentResponse[];
  hasAttachment: boolean;
  isMissionStarted: boolean;
  author: MissionLectureAuthorOneResponse;
  mainInstructor: MainInstructor;
  startedAt?: string;
  endedAt?: string;
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface MySubmissionStatusResponse {
  submissionStatus: 'NONE' | 'DRAFT' | 'REJECTED' | 'SUBMITTED' | 'ACCEPTED';
  submissionId?: number;
}

export interface MissionLectureAuthor {
  id: number;
  name: string;
  slug?: string;
  thumbnailUrl: string;
}

export interface MissionLectureOneAttachment {
  type: 'LINK' | 'IMAGE' | 'FILE';
  url?: string;
  fileId?: number;
  name?: string;
  value: string;
}

export interface MissionLectureOneByInstructorResponse {
  id: number;
  title: string;
  missionId: number;
  body: string;
  hasAutoFeedback: boolean;
  autoFeedbackBody?: string;
  attachments: MissionLectureOneAttachment[];
  hasAttachment: boolean;
  author: MissionLectureAuthor;
  startedAt?: string;
  endedAt?: string;
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface GetSummariesByCourseItem {
  missionId: number;
  missionLectureId: number;
  title: string;
}

export interface GetSummariesByCourseResponse {
  items: GetSummariesByCourseItem[];
}

export interface ApiResponseSearchPartnerCourseResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: SearchPartnerCourseResponse;
}

export interface CommissionRateResponse {
  agreed: number;
  disagreed: number;
}

export interface PagePartnerCourseResponse {
  totalElements?: number;
  totalPages?: number;
  size?: number;
  content?: PartnerCourseResponse[];
  number?: number;
  sort?: SortObject;
  first?: boolean;
  last?: boolean;
  numberOfElements?: number;
  pageable?: PageableObject;
  empty?: boolean;
}

export interface PartnerCourseInfoResponse {
  id: number;
  slug: string;
  thumbnailUrl: string;
  title: string;
  description: string;
  reviewCount: number;
  studentCount: number;
  likeCount: number;
  star: number;
  updatedAt: string;
  publishedAt?: string;
  updatedAtUTC: string;
  publishedAtUTC?: string;
  metadata: PartnerCourseMetadataResponse;
  abilities: string[];
  targets: string[];
  runtimeSecond: number;
}

export interface PartnerCourseInstructorResponse {
  id: number;
  name: string;
}

export interface PartnerCourseMetadataResponse {
  level: string;
  levelCode: string;
  skills: string[];
  skillTags: SearchPartnerCourseSkillTagResponse[];
  categories: SearchPartnerCourseCategoryResponse[];
}

export interface PartnerCoursePriceResponse {
  isDiscount: boolean;
  originalPrice: number;
  payPrice: number;
  regularPrice: number;
  discountRate: number;
  discountEndedAt?: string;
  discountEndedAtUTC?: string;
  discountId?: number;
  discountTitle?: string;
}

export interface PartnerCourseResponse {
  course: PartnerCourseInfoResponse;
  listPrice: PartnerCoursePriceResponse;
  instructor: PartnerCourseInstructorResponse;
  commissionRate: number;
  expectedRevenue: number;
}

export interface SearchPartnerCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parent: ParentCategoryResponse;
}

export interface SearchPartnerCourseResponse {
  page?: PagePartnerCourseResponse;
  partner: SearchPartnerResponse;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: PartnerCourseResponse[];
}

export interface SearchPartnerCourseSkillTagResponse {
  id: number;
  title: string;
  slug: string;
}

export interface SearchPartnerResponse {
  id: number;
  type: 'BUSINESS' | 'PERSONAL';
  commissionRate: CommissionRateResponse;
  hasSettlementInfo: boolean;
}

export interface ApiResponseGetMarketingPartnerLinkCreatePreviewResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: GetMarketingPartnerLinkCreatePreviewResponse;
}

export interface GetMarketingPartnerLinkCreatePreviewResponse {
  canCreate: boolean;
  commissionRate: number;
  attributeWindowDays: number;
}

export interface LiveSessionUnit {
  id: number;
  title: string;
  recordingOption: 'PROVIDED' | 'NOT_PROVIDED';
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface LiveSessionUnitsResponse {
  title: string;
  units: LiveSessionUnit[];
}

export interface GetLiveSessionAttendedResponse {
  isAttended: boolean;
}

export interface AttachmentFile {
  id: number;
  name: string;
}

export interface GetLiveSessionUnitResponse {
  id: number;
  title: string;
  body: string;
  liveSessionId: number;
  attachment?: AttachmentFile;
  hasAttachment: boolean;
  status: 'NOT_STARTED' | 'ON_LIVE' | 'ENDED';
  type: 'STANDARD' | 'LARGE_SCALE';
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface GetLiveSessionUnitManageResponse {
  id: number;
  title: string;
  body: string;
  recordingOption: 'PROVIDED' | 'NOT_PROVIDED';
  attachment?: LiveSessionUnitAttachmentResponse;
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface LiveSessionUnitAttachmentResponse {
  id: number;
  name: string;
}

export interface LectureAttachmentOneResponse {
  id: number;
  name: string;
}

export interface LectureOneResponse {
  title: string;
  body: string;
  isPreview: boolean;
  video?: LectureVideoOneResponse;
  attachment?: LectureAttachmentOneResponse;
}

export interface LectureVideoOneResponse {
  id: number;
  name: string;
}

export interface GetKeywordNotificationSubscriptionResponse {
  isAlreadySubscription: boolean;
}

export interface GetRecommendedInstructorsResponse {
  items: RecommendedInstructorDto[];
}

export interface RecommendedInstructorDto {
  id: number;
  name: string;
  slug?: string;
  thumbnail: string;
}

export interface AvailableDiscountRange {
  minSupplyPrice: number;
  maxSupplyPrice: number;
}

export interface CourseDetailInfo {
  id: number;
  title: string;
  slug: string;
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  vodOriginalLanguage:
    | 'KO'
    | 'EN'
    | 'JA'
    | 'VI'
    | 'CN'
    | 'TW'
    | 'HI'
    | 'FR'
    | 'CA'
    | 'ES'
    | 'MX'
    | 'PT';
  type: 'ON' | 'OFF' | 'CHALLENGE';
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  price?: number;
  regularPrice?: number;
  sourceCurrency: 'KRW' | 'USD' | 'VND' | 'JPY';
  promotionCode: string;
  isPromotion: boolean;
  isExclusive: boolean;
  thumbnailUrl: string;
  reviewCount: number;
  totalStar: number;
  studentCount: number;
  unAnsweredQuestionCount: number;
  discountInfo?: PaginatedCourseDiscountInfo;
  availableDiscountRange?: AvailableDiscountRange;
  contractStatus?: 'DRAFT' | 'NOT_SIGNED' | 'CANCELED' | 'SIGNED' | 'TERMINATED' | 'NO_REQUIRED';
  contractType?: 'PLATFORM' | 'INFLUENCER' | 'OFFLINE' | 'E' | 'EXCLUSIVE' | 'NONEXCLUSIVE';
  publishedAtUTC?: string;
  instructor: PaginatedCourseByUserInstructorInfo;
  offlineCourse?: OfflineCourseInfo;
  challengeCourse?: PaginatedCourseByUserChallengeCourseInfo;
}

export interface OfflineCourseInfo {
  id: number;
  type: 'CONFERENCE' | 'CLASS';
  capacity: number;
  startedAt: string;
  endedAt: string;
  applyStartedAt: string;
  applyEndedAt: string;
  trainingExpenses?: number;
  isKdt: boolean;
}

export interface PaginatedCourseByUserChallengeCourseInfo {
  id: number;
  capacity: number;
  startedAt?: string;
  endedAt?: string;
  applyStartedAt?: string;
  applyEndedAt?: string;
  startedAtUTC?: string;
  endedAtUTC?: string;
  applyStartedAtUTC?: string;
  applyEndedAtUTC?: string;
}

export interface PaginatedCourseByUserDetailResponse {
  items: CourseDetailInfo[];
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface PaginatedCourseByUserInstructorInfo {
  userId: number;
  isRepresentative: boolean;
}

export interface PaginatedCourseDiscountInfo {
  isEarlyBirdDiscountOngoing: boolean;
  discountStatus: 'CAN_DISCOUNT' | 'ON_DISCOUNT' | 'COOL_TIME' | 'UNAVAILABLE';
  discountedPrice: number;
  discountedRegularPrice: number;
  startedAt: string;
  endedAt: string;
  startedAtUTC: string;
  endedAtUTC: string;
  coolTimeEndsAtUTC?: string;
}

export interface GetIsInstructorResponse {
  isInstructor: boolean;
}

export interface MainInstructorResponse {
  id: number;
  name: string;
  slug: string;
  introduce: string;
  thumbnail: string;
  isRepresentative: boolean;
}

export interface MainInstructorsResponse {
  items: MainInstructorResponse[];
}

export interface CouponAvailableCourseByInstructorDto {
  courseId: number;
  title: string;
  price: number;
  surtaxPrice: number;
}

export interface FindCouponAvailableCourseForInstructorResponse {
  items: CouponAvailableCourseByInstructorDto[];
}

export interface GetInstructorByEmailResponse {
  id: number;
  name: string;
  thumbnailImageUrl: string;
  isAdmin: boolean;
  isInstructor: boolean;
}

export interface GetCourseInstructorSettingsResponse {
  courseId: number;
  instructors: GetInstructorSettingsItem[];
  whetherAnswer: boolean;
}

export interface GetInstructorSettingsItem {
  userId: number;
  sequence: number;
  name: string;
  type: 'MAIN' | 'SUB';
  isQuestionEmailAllowed: boolean;
  thumbnailImageUrl: string;
  isRepresentative: boolean;
}

export interface GetGiftPhotoCardsResponse {
  items: GiftPhotoCardResponse[];
}

export interface GiftPhotoCardResponse {
  id: number;
  title: string;
  url: string;
  isDefault: boolean;
}

export interface FindBestDiscountResponse {
  discountType: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
  discountId?: number;
  discountTitle: string;
  endedAtUTC?: string;
  paymentPrice: number;
  discountRate: number;
}

export interface AttachmentOneResponse {
  name: string;
}

export interface CurriculumLectureOneResponse {
  id: number;
  isPreview: boolean;
  isVisible: boolean;
  hasAutoFeedback: boolean;
  sequence: number;
  hasVideo: boolean;
  hasAttachment: boolean;
  title: string;
  type: string;
  video?: VideoOneResponse;
  runtime?: number;
  attachment?: AttachmentOneResponse;
  attachmentType: 'NONE' | 'LINK' | 'IMAGE_FILE' | 'ATTACHMENT_FILE';
  quizId?: number;
  isChallengeOnly: boolean;
  missionStartedAtUTC?: string;
  missionEndedAtUTC?: string;
  liveSession?: LiveSessionCurriculumResponse;
}

export interface CurriculumResponse {
  curriculum: SectionOneResponse[];
}

export interface LiveSessionCurriculumResponse {
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface SectionOneResponse {
  id: number;
  title: string;
  sequence: number;
  isVisible: boolean;
  units: CurriculumLectureOneResponse[];
}

export interface VideoOneResponse {
  name: string;
}

export interface ApiResponseCourseSimpleOneResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: CourseSimpleOneResponse;
}

export interface CourseSimpleOneResponse {
  id: number;
  title: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  slug?: string;
  regularPrice?: number;
  duration?: number;
  studentCount: number;
  publishedAt?: string;
  submittedAt?: string;
  challengeCourse?: ChallengeCourseResponse;
  publishedAtUTC?: string;
  submittedAtUTC?: string;
}

export interface CourseVoucherResponse {
  isValid: boolean;
  lastUnitId: number;
  progress: string;
  completeCount: number;
}

export interface GetCourseDetailInfoResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  id: number;
  slug?: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  thumbnailUrl: string;
  isNew: boolean;
  isBest: boolean;
  title: string;
  studentCount: number;
  duration?: number;
  providesCertificate: boolean;
  providesInstructorAnswer: boolean;
  providesInquiry: boolean;
  isLiked: boolean;
  likeCount: number;
  description: string;
  unitSummary: UnitSummaryResponse;
  groupEnrolment?: GroupEnrolmentResponse;
  cart?: CartResponse;
  category: CourseCategoryResponse;
  voucher?: CourseVoucherResponse;
  paymentInfo: CoursePaymentResponse;
  review: ReviewResponse;
  instructors: CourseInstructorResponse[];
  levels: CourseLevelResponse[];
  skillTags: CourseSkillOneResponse[];
  publishedAt?: string;
  lastUpdatedAt?: string;
  commonTags: CommonTagResponse[];
  isGiftable?: boolean;
  isRequestGiftable: boolean;
  courseType: 'ON' | 'OFF' | 'CHALLENGE';
  challenge?: BaseCourseChallengeResponse;
  publishedAtUTC?: string;
  lastUpdatedAtUTC: string;
}

export interface DraftCourseCount {
  online: number;
  offline: number;
  challenge: number;
}

export interface GetCourseCreationAvailabilityResponse {
  canCreateCourse: boolean;
  draftCourseCount: DraftCourseCount;
}

export interface GetChatbotRecommendedCoursesResponse {
  courses: CourseCardItem[];
}

export interface CourseAccessInfoResponse {
  id: number;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  slug: string;
  enSlug?: string;
  accessibleMenu: CourseAccessibleMenuResponse;
}

export interface CourseAccessibleMenuResponse {
  intro: boolean;
  dashboard: boolean;
  inquiries: boolean;
  community: boolean;
  news: boolean;
  manage: boolean;
  edit: boolean;
}

export interface CourseOneLevelResponse {
  level: string;
  levelCode: string;
}

export interface CourseOneResponse {
  id: number;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  title: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  slug: string;
  price?: number;
  duration?: number;
  courseType: 'ONLINE' | 'CLASS' | 'CONFERENCE' | 'CHALLENGE';
  skills: CourseSkillOneResponse[];
  representativeInstructor: CourseInstructorResponse;
  category: CourseCategoryTitleResponse;
  thumbnailUrl: string;
  levels: CourseOneLevelResponse[];
  discountedPrice?: number;
  discountStartedAtUTC?: string;
  discountEndedAtUTC?: string;
  instructorCount: number;
  studentCount: number;
  targetPublishAtUTC?: string;
  submittedAtUTC?: string;
}

export interface StudyProgressResponse {
  totalUnitCount: number;
  isCertificateIssued: boolean;
  isCertificateProvided: boolean;
  completeCount: number;
  progress: string;
  totalCompleteRuntime: number;
  voucherId: number;
  lastUnitId: number;
}

export interface RelatedChallengeCourseResponse {
  items: CourseCardItem[];
}

export interface GetOnlineCourseInfoResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  id: number;
  slug: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  thumbnailUrl: string;
  isNew: boolean;
  isBest: boolean;
  title: string;
  studentCount: number;
  duration?: number;
  providesCertificate: boolean;
  providesInstructorAnswer: boolean;
  providesInquiry: boolean;
  isLiked: boolean;
  likeCount: number;
  description: string;
  unitSummary: UnitSummaryResponse;
  groupEnrolment?: GroupEnrolmentResponse;
  cart?: CartResponse;
  category: CourseCategoryResponse;
  voucher?: CourseVoucherResponse;
  paymentInfo: CoursePaymentResponse;
  review: ReviewResponse;
  instructors: CourseInstructorResponse[];
  levels: CourseLevelResponse[];
  skillTags: CourseSkillOneResponse[];
  publishedAt?: string;
  lastUpdatedAt: string;
  commonTags: CommonTagResponse[];
  isGiftable: boolean;
  isRequestGiftable: boolean;
  publishedAtUTC?: string;
  lastUpdatedAtUTC: string;
}

export interface GetOfflineCourseInfoResponse {
  id: number;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  thumbnailUrl: string;
  isNew: boolean;
  isBest: boolean;
  title: string;
  isLiked: boolean;
  likeCount: number;
  providesInquiry: boolean;
  offlineInfo: OfflineCourseResponse;
  category: CourseCategoryResponse;
  voucher?: OfflineCourseVoucherResponse;
  paymentInfo: CoursePaymentResponse;
  instructors: CourseInstructorResponse[];
  levels: CourseLevelResponse[];
  skillTags: CourseSkillOneResponse[];
}

export interface KdtResponse {
  trainingExpenses: number;
}

export interface OfflineCourseResponse {
  applyStartedAt: string;
  applyEndedAt: string;
  startedAt: string;
  endedAt: string;
  capacity: number;
  applicantCount: number;
  type: 'CONFERENCE' | 'CLASS';
  kdt?: KdtResponse;
  survey: SurveyResponse;
  locationType: 'ONLINE' | 'OFFLINE';
  onlineMeetingLink?: string;
  offlineAddressMain?: string;
  offlineAddressDetail?: string;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface OfflineCourseVoucherResponse {
  isValid: boolean;
}

export interface CourseMetaResponse {
  description: string;
  keywords: string;
  categorySlugs: string[];
  skillSlugs: string[];
  commonTagsSlugs: string[];
  exposure: boolean;
}

export interface GetChallengeCourseInfoResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  id: number;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  thumbnailUrl: string;
  isNew: boolean;
  isBest: boolean;
  title: string;
  studentCount: number;
  duration?: number;
  providesCertificate: boolean;
  providesInstructorAnswer: boolean;
  providesInquiry: boolean;
  isLiked: boolean;
  likeCount: number;
  description: string;
  unitSummary: UnitSummaryResponse;
  groupEnrolment?: GroupEnrolmentResponse;
  cart?: CartResponse;
  category: CourseCategoryResponse;
  voucher?: CourseVoucherResponse;
  paymentInfo: CoursePaymentResponse;
  review: ReviewResponse;
  instructors: CourseInstructorResponse[];
  levels: CourseLevelResponse[];
  skillTags: CourseSkillOneResponse[];
  publishedAt?: string;
  lastUpdatedAt?: string;
  startedAt?: string;
  endedAt?: string;
  applyStartedAt?: string;
  applyEndedAt?: string;
  capacity: number;
  appliedStudentCount: number;
  publishedAtUTC?: string;
  lastUpdatedAtUTC: string;
  startedAtUTC: string;
  endedAtUTC: string;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
}

export interface ApiResponseUserProfileCourseResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: UserProfileCourseResponse;
}

export interface UserProfileCourseResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseCardItem[];
}

export interface CourseBySkillIdsResponse {
  items: CourseCardItem[];
  attributeToken: string;
}

export interface CourseShortCodeResponse {
  items: CourseCardItem[];
}

export interface ApiResponseCourseByLikesResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: CourseByLikesResponse;
}

export interface CourseByLikesResponse {
  items: CourseCardItem[];
}

export interface CourseByInstructorIdResponse {
  items: CourseCardItem[];
}

export interface CourseAutoCompleteResponse {
  items: string[];
  attributionToken: string;
}

export interface FindSurveyChoice {
  id: number;
  content: string;
  sequence: number;
}

export interface FindSurveyQuestion {
  id: number;
  content: string;
  sequence: number;
  isRequired: boolean;
  isDefaultQuestion: boolean;
  type: 'SUBJECTIVE' | 'OBJECTIVE' | 'MULTIPLE_OBJECTIVE' | 'FILE';
  choices: FindSurveyChoice[];
}

export interface FindSurveyQuestionResponse {
  surveyQuestions: FindSurveyQuestion[];
}

export interface FindSurveyAnswerUploadedFileResponse {
  url: string;
}

export interface Choice {
  id: number;
  content: string;
  sequence: number;
  isSelected: boolean;
}

export interface FindCourseSurveyByUserCourseResponse {
  id: number;
  title: string;
  challengeCourse: FindCourseSurveyChallengeCourseInfo;
  instructor: FindCourseSurveyInstructorInfo;
}

export interface FindCourseSurveyByUserOneResponse {
  questionId: number;
  questionSequence: number;
  questionContent: string;
  isDefault: boolean;
  isRequired: boolean;
  isAnswered: boolean;
  type: 'OBJECTIVE' | 'MULTIPLE_OBJECTIVE' | 'SUBJECTIVE' | 'FILE';
  answerContent?: string;
  choices: Choice[];
  isFileUploaded: boolean;
  fileName?: string;
}

export interface FindCourseSurveyByUserResponse {
  course: FindCourseSurveyByUserCourseResponse;
  surveyQuestions: FindCourseSurveyByUserOneResponse[];
}

export interface FindCourseSurveyChallengeCourseInfo {
  startedAt: string;
  endedAt: string;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface FindCourseSurveyInstructorInfo {
  name: string;
}

export interface CourseSkillDto {
  id: number;
  title?: string;
  subTitle?: string;
  slug?: string;
}

export interface GetCourseSkillsResponse {
  courseId: number;
  skills: CourseSkillDto[];
}

export interface GetCourseSettingsResponse {
  id: number;
  price?: number;
  completeMessage?: string;
  startMessage?: string;
  whetherAnswer: boolean;
  hasInquiry: boolean;
  questionGuide?: string;
  duration?: number;
  exposure: boolean;
  isB2BAvailable: boolean;
  b2bPrice?: number;
  b2bPriceNextSyncAtUTC?: string;
  sourceCurrency: 'KRW' | 'USD' | 'VND' | 'JPY';
}

export interface GetChallengeCourseSettingsResponse {
  id: number;
  price?: number;
  exposure: boolean;
  capacity: number;
  minCapacity: number;
  hasInquiry: boolean;
  whetherAnswer: boolean;
  questionGuide?: string;
  inquiryEmail?: string;
  inquiryLink?: string;
}

export interface CourseRecommendationResponse {
  items: CourseCardItem[];
}

export interface FreeCourseRecommendationResponse {
  items: CourseCardItem[];
}

export interface ChallengeCourseBenefitResponse {
  title: string;
  type: string;
  iconType: string;
  isDefault: boolean;
  isSelected: boolean;
}

export interface ChallengeCourseBenefitsResponse {
  benefits: ChallengeCourseBenefitResponse[];
}

export interface CourseInfoSkillTagDto {
  id: number;
  subTitle?: string;
  slug: string;
  relatedCount: number;
  title: string;
}

export interface GetCourseInfoResponse {
  id: number;
  abilities: string[];
  based: string[];
  categoryIds: number[];
  mainCategoryId?: number;
  description: string;
  levelIds: number[];
  /**
   * use skillTags instead
   */
  skillTagIds: number[];
  targets: string[];
  title: string;
  skillTags: CourseInfoSkillTagDto[];
  commonTagIds: number[];
  commonTags: CommonTagResponse[];
  challenge?: ChallengeCourseBenefitsResponse;
}

export interface FindOneSentCourseGiftDiscountInfo {
  id?: number;
  title: string;
  discountRate: number;
}

export interface FindOneSentCourseGiftInstructorInfo {
  id: number;
  name: string;
}

export interface FindOneSentCourseGiftResponse {
  id: number;
  orderId: number;
  orderTitle: string;
  status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  senderUserId: number;
  senderName: string;
  receiverName: string;
  receiverPhoneNumber?: string;
  message: string;
  giftPhotoCardId: number;
  giftPhotoCardImageUrl: string;
  code: string;
  refundLimitedAt: string;
  canceledAt?: string;
  isRefundable: boolean;
  giftSendType: 'PHONE' | 'LINK';
  course: FindOneSentCourseInfo;
  paymentInfo: FindOneSentCoursePaymentInfo;
  refundLimitedAtUTC: string;
  canceledAtUTC?: string;
}

export interface FindOneSentCourseInfo {
  id: number;
  title: string;
  slug: string;
  instructor: FindOneSentCourseGiftInstructorInfo;
}

export interface FindOneSentCoursePaymentInfo {
  isReceiptAvailable: boolean;
  receiptUrl: string;
  paymentPrice: number;
  paidAt?: string;
  payMethod?:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'SAMSUNGPAY'
    | 'APPLEPAY'
    | 'PAYCO'
    | 'VIRTUAL_ACCOUNT'
    | 'BANK_TRANSFER'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'VBANK'
    | 'CARD_LEGACY'
    | 'NAVERPAY_LEGACY'
    | 'POINT'
    | 'PHONE'
    | 'EMPTY'
    | 'NONE'
    | 'FPX'
    | 'SOFORT'
    | 'PAYNOW'
    | 'GRABPAYPH'
    | 'GRABPAYMY'
    | 'BOOST'
    | 'TOUCHNGO'
    | 'KONBINI'
    | 'PAYSHOP'
    | 'BANCONTACT'
    | 'EPS'
    | 'MULTIBANCO'
    | 'PRZELEWY24'
    | 'BLIK'
    | 'IDEAL'
    | 'PLATFORM'
    | 'POLI'
    | 'INSTANT_BANK_TRANSFER'
    | 'UPI';
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  pg?:
    | 'KAKAOPAY'
    | 'NAVERPAY'
    | 'TOSS_WIDGET'
    | 'TOSS'
    | 'TOSS_V2'
    | 'TOSS_GLOBAL'
    | 'TOSS_PAY_LEGACY'
    | 'TOSS_PAY'
    | 'IMP_KAKAOPAY'
    | 'IMP_NAVERPAY'
    | 'IMP_NICE'
    | 'IMP_JTNET'
    | 'PORTONE';
  discountInfo?: FindOneSentCourseGiftDiscountInfo;
  paidAtUTC?: string;
}

export interface ApiResponseFindSentCourseGiftResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: FindSentCourseGiftResponse;
}

export interface FindSentCourseGiftItem {
  id: number;
  receiptUrl: string;
  status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  createdAt: string;
  orderTitle: string;
  courseTitle: string;
  courseSlug: string;
  receiverName: string;
  isDeletedCourse: boolean;
  isRefundable: boolean;
  refundLimitedAt: string;
  regularPrice: number;
  paymentPrice: number;
  currency: 'KRW' | 'USD' | 'VND' | 'JPY';
  timezone: string;
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  isReceiptAvailable: boolean;
  isInvoiceAvailable: boolean;
  createdAtUTC: string;
  refundLimitedAtUTC?: string;
}

export interface FindSentCourseGiftResponse {
  items: FindSentCourseGiftItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPage: number;
}

export interface ApiResponseFindReceivedCourseGiftResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: FindReceivedCourseGiftResponse;
}

export interface FindReceivedCourseGiftItem {
  id: number;
  code: string;
  giftStatus?: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  giftPhotoCardId: number;
  giftPhotoCardImageUrl: string;
  createdAt: string;
  orderTitle: string;
  courseTitle: string;
  courseSlug: string;
  isDeletedCourse: boolean;
  senderName: string;
  limitedAt?: string;
  canceledAt?: string;
  approvedAt: string;
  duration?: number;
  createdAtUTC: string;
  limitedAtUTC?: string;
  approvedAtUTC: string;
  canceledAtUTC?: string;
  isCanceled: boolean;
  status: 'RECEIVED' | 'CANCELED';
}

export interface FindReceivedCourseGiftResponse {
  items: FindReceivedCourseGiftItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPage: number;
}

export interface BuyerInfo {
  name: string;
  phone: string;
  email: string;
  countryCode: string;
}

export interface CourseGiftItem {
  id: number;
  giftStatus?: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  message: string;
  giftPhotoCardId: number;
  giftPhotoCardImageUrl: string;
  code: string;
  giftSendType: 'PHONE' | 'LINK';
  receiver: CourseGiftOrderCompleteReceiver;
  status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'CANCELED';
}

export interface CourseGiftOrderCompleteReceiver {
  name: string;
  phoneNumber?: string;
}

export interface CourseGiftOrderCompleteResponse {
  id: number;
  title: string;
  refundLimitedAt: string;
  canceledAt?: string;
  courseGift: CourseGiftItem;
  course: CourseGiftOrderCourseItem;
  mainInstructor: MainInstructor;
  paymentInfo: CourseGiftPaymentInfo;
  refundLimitedAtUTC: string;
  canceledAtUTC?: string;
}

export interface CourseGiftOrderCourseItem {
  id: number;
  title: string;
  slug: string;
}

export interface CourseGiftPaymentInfo {
  isReceiptAvailable: boolean;
  receiptUrl: string;
  paymentPrice: number;
  paidAt: string;
  payMethod:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'SAMSUNGPAY'
    | 'APPLEPAY'
    | 'PAYCO'
    | 'VIRTUAL_ACCOUNT'
    | 'BANK_TRANSFER'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'VBANK'
    | 'CARD_LEGACY'
    | 'NAVERPAY_LEGACY'
    | 'POINT'
    | 'PHONE'
    | 'EMPTY'
    | 'NONE'
    | 'FPX'
    | 'SOFORT'
    | 'PAYNOW'
    | 'GRABPAYPH'
    | 'GRABPAYMY'
    | 'BOOST'
    | 'TOUCHNGO'
    | 'KONBINI'
    | 'PAYSHOP'
    | 'BANCONTACT'
    | 'EPS'
    | 'MULTIBANCO'
    | 'PRZELEWY24'
    | 'BLIK'
    | 'IDEAL'
    | 'PLATFORM'
    | 'POLI'
    | 'INSTANT_BANK_TRANSFER'
    | 'UPI';
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  pg?:
    | 'KAKAOPAY'
    | 'NAVERPAY'
    | 'TOSS_WIDGET'
    | 'TOSS'
    | 'TOSS_V2'
    | 'TOSS_GLOBAL'
    | 'TOSS_PAY_LEGACY'
    | 'TOSS_PAY'
    | 'IMP_KAKAOPAY'
    | 'IMP_NAVERPAY'
    | 'IMP_NICE'
    | 'IMP_JTNET'
    | 'PORTONE';
  priceBreakdown: PriceBreakdown;
  buyer: BuyerInfo;
  paidAtUTC: string;
}

export interface Discount {
  id?: number;
  title: string;
  rate: number;
  amount: number;
}

export interface PriceBreakdown {
  coursePrice: number;
  regularPrice: number;
  sellingPrice: number;
  discount?: Discount;
}

export interface FindOneCourseGiftCourseInfo {
  id: number;
  title: string;
  slug: string;
  coverFileUrl: string;
  duration?: number;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  isDeleted: boolean;
  mainInstructor: MainInstructorInfo;
}

export interface FindOneCourseGiftResponse {
  id: number;
  orderTitle: string;
  status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  senderName: string;
  message: string;
  sendType: 'PHONE' | 'LINK';
  approvedAt?: string;
  approvedLimitedAt: string;
  canceledAt?: string;
  isApproveAvailable: boolean;
  alreadyHasVoucher: boolean;
  isMyReceived: boolean;
  receivedUserId?: number;
  giftPhotoCard: GiftPhotoCardInfo;
  loginUser?: LoginUser;
  course: FindOneCourseGiftCourseInfo;
  courseGiftVoucher?: FindOneCourseGiftVoucherInfo;
  approvedAtUTC?: string;
  approvedLimitedAtUTC: string;
  canceledAtUTC?: string;
}

export interface FindOneCourseGiftVoucherInfo {
  id: number;
  limitedAt?: string;
  limitedAtUTC?: string;
}

export interface GiftPhotoCardInfo {
  id: number;
  url: string;
}

export interface LoginUser {
  name: string;
  email: string;
  isBlocked: boolean;
}

export interface MainInstructorInfo {
  userId: number;
  name: string;
}

export interface GetCourseFaqResponse {
  id: number;
  title: string;
  body: string;
}

export interface GetCourseFaqsResponse {
  hasFaq?: boolean;
  items: GetCourseFaqResponse[];
}

export interface GetCourseCoverVodResponse {
  url?: string;
  id?: number;
  fileName?: string;
  metadata?: CourseVideoMetadata;
}

export interface GetCourseCoverImageResponse {
  url: string;
  id?: number;
  metadata?: CourseCoverMetadata;
}

export interface GetCourseCoverAiGenerationRetryInfoResponse {
  retryable: boolean;
  availableRetryCount: number;
}

export interface GetCourseCoverAiGenerationHistoriesResponse {
  items: HistoryItem[];
}

export interface HistoryItem {
  id: number;
  requestedAt: string;
  completedAt: string;
  templateId: number;
  templateTitle: string;
  compositeImageFileName?: string;
  compositeImageUrl?: string;
  images: ImageItem[];
}

export interface ImageItem {
  id?: number;
  url?: string;
  isDisliked: boolean;
}

export interface GetCourseCoverAiGenerationLatestHistoryResponse {
  id?: number;
  status: 'NONE' | 'NOT_REQUESTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
}

export interface CourseCoverAiGenerationTemplateItem {
  id: number;
  title: string;
  description: string;
  requiredImageType: 'NONE' | 'PERSON';
  thumbnailUrl: string;
  templateKey: string;
  exampleImageItems: ExampleImageItem[];
}

export interface ExampleImageItem {
  url: string;
}

export interface GetCourseCoverAiGenerationTemplatesResponse {
  items: CourseCoverAiGenerationTemplateItem[];
}

export interface ContractInfo {
  current?: CurrentContract;
  defaults: DefaultContractResponse[];
}

export interface CurrentContract {
  contractType: 'PLATFORM' | 'INFLUENCER' | 'OFFLINE' | 'E' | 'EXCLUSIVE' | 'NONEXCLUSIVE';
  isExclusive: boolean;
  isPromotion: boolean;
  isInflearnMarketing: boolean;
  inflearnMarketingConfirmedAt?: string;
  rate: RateInfo;
  inflearnMarketingConfirmedAtUTC?: string;
}

export interface DefaultContractResponse {
  contractType: 'PLATFORM' | 'INFLUENCER' | 'OFFLINE' | 'E' | 'EXCLUSIVE' | 'NONEXCLUSIVE';
  isPromotion?: boolean;
  isExclusive?: boolean;
  allowedUsageAi: boolean;
  rate: RateInfo;
}

export interface GetCourseContractCourseInfo {
  id: number;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  title: string;
  price?: number;
  isB2bAvailable: boolean;
  allowedUsageAi?: boolean;
  isB2bSubscription: boolean;
}

export interface GetCourseContractResponse {
  course: GetCourseContractCourseInfo;
  contract: ContractInfo;
}

export interface RateInfo {
  baseRate?: number;
  b2bRate?: number;
  promotionLinkRate?: number;
  openingMonthRate?: number;
  instructorCouponRate?: number;
  inflearnMarketingRate?: number;
  additionalMarketingRate?: number;
}

export interface GetCourseContractAgreementDetail {
  contractType: 'PLATFORM' | 'INFLUENCER' | 'OFFLINE' | 'E' | 'EXCLUSIVE' | 'NONEXCLUSIVE';
  salesTermAgreement: boolean;
  exclusiveTermAgreement: boolean;
  b2bSubscriptionAgreement: boolean;
  inflearnMarketingAgreement: boolean;
  contentTermAgreement: boolean;
}

export interface GetCourseContractBankAccountDetail {
  bank: string;
  bankAccountHolder: string;
  bankAccountNumber: string;
}

export interface GetCourseContractDetailResponse {
  courseId: number;
  courseTitle: string;
  courseType: string;
  contractedAt?: string;
  contractedAtUTC?: string;
  contractDurationMonth: number;
  contractAgreement: GetCourseContractAgreementDetail;
  contractor: GetCourseContractorDetail;
  bankAccount: GetCourseContractBankAccountDetail;
}

export interface GetCourseContractorDetail {
  subjectType?: 'WITHHOLDING_TAX' | 'NON_WITHHOLDING_TAX' | 'BUSINESS';
  instructorName: string;
  name: string;
  rrn: string;
  brn: string;
  paypalLink: string;
}

export interface GetInflearnMarketingContentTermResponse {
  content: string;
}

export interface GetCourseContractContentTermResponse {
  term: string;
  termType: 'NEW_CONTRACT' | 'MODIFICATION_CONTRACT';
  courseType: 'ON' | 'OFF' | 'CHALLENGE';
}

export interface FindInflearnMarketingConfirmRequiredCoursesResponse {
  items: InflearnMarketingConfirmRequiredItem[];
}

export interface InflearnMarketingConfirmRequiredItem {
  id: number;
  title: string;
}

export interface FindCourseContentResponse {
  jsonBody: string;
  courseOriginalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
}

export interface FindCourseContentTemplateResponse {
  id: number;
  title: string;
  authorType: 'INFLAB' | 'INSTRUCTOR';
  htmlBody: string;
  jsonBody: string;
  createdAt: string;
  thumbnailUrl?: string;
  createdAtUTC: string;
}

export interface OffsetPageResponseFindCourseContentTemplateResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: FindCourseContentTemplateResponse[];
}

export interface CourseContentHistoryOrigin {
  id: number;
  name: string;
  savedAt: string;
  type: string;
  userName: string;
  userId: number;
  savedAtUTC: string;
}

export interface FindCourseContentHistoryResponse {
  id: number;
  name: string;
  savedAt: string;
  type: string;
  userName: string;
  userId: number;
  origin?: CourseContentHistoryOrigin;
  savedAtUTC: string;
}

export interface FindOneCourseContentHistoryResponse {
  id: number;
  name: string;
  savedAt: string;
  userName: string;
  type: string;
  jsonBody: string;
  savedAtUTC: string;
}

export interface GetLatestCourseContentGenerationHistoryResponse {
  id?: number;
  templateId?: number;
  jsonBody?: string;
  status: 'NONE' | 'NOT_REQUESTED' | 'COMPLETED' | 'FAILED';
}

export interface CourseContentGenerationTemplateItem {
  id: number;
  title: string;
  description: string;
  requiredInfoType: 'NONE' | 'INSTRUCTOR_PROFILE';
  templateKey: 'PROBLEM' | 'RESULT' | 'STORY';
  blocks: TemplateItemComponentBlock[];
}

export interface GetCourseContentGenerationTemplatesResponse {
  items: CourseContentGenerationTemplateItem[];
}

export interface TemplateItemComponentBlock {
  title: string;
  type: 'NORMAL' | 'HOOKING';
}

export interface ApiResponseListGetCertificatesResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: GetCertificatesResponse[];
}

export interface GetCertificatesResponse {
  userId: number;
  voucherId: number;
  courseId: number;
  courseSlug: string;
  courseTitle: string;
  courseType: 'ON' | 'OFF' | 'CHALLENGE';
  courseThumbnailUrl: string;
  certificateConfirmedAt?: string;
  certificatedAt?: string;
  certificateConfirmedAtUTC?: string;
  certificatedAtUTC?: string;
}

export interface ApiResponseUnconfirmedResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: UnconfirmedResponse;
}

export interface UnconfirmedResponse {
  hasUnconfirmedCertificate: boolean;
}

export interface ApiResponseGetCertificateDetailResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: GetCertificateDetailResponse;
}

export interface GetCertificateDetailResponse {
  userId: number;
  courseId: number;
  voucherId: number;
  realName: string;
  title: string;
  runtime: number;
  instructorName: string;
  startedAt: string;
  certificatedAt: string;
  startedAtUTC: string;
  certificatedAtUTC: string;
}

export interface CouponDiscountResponse {
  type: 'PERCENT' | 'PRICE';
  value: number;
}

export interface CouponItem {
  couponId: number;
  couponUserId: number;
  title: string;
  discount: CouponDiscountResponse;
  target: CouponTargetResponse;
  endedAt: string;
  canResurrect: boolean;
  isAvailable: boolean;
  endedAtUTC: string;
}

export interface CouponTargetResponse {
  type: 'ALL' | 'COURSE' | 'ROADMAP';
  courseIds: number[];
  requiredCourseIds?: number[];
  roadmap?: RaodmapWithProgress;
}

export interface GetCouponsResponse {
  items: CouponItem[];
}

export interface RaodmapWithProgress {
  id: number;
  title: string;
  progress: number;
  enrolledCount: number;
  likeCount: number;
  commentCount: number;
  items: RoadmapItemSummary[];
}

export interface RoadmapCourse {
  id: number;
}

export interface RoadmapItemSummary {
  id: number;
  title: string;
  type: 'COURSE' | 'BOOK' | 'LINK';
  course?: RoadmapCourse;
}

export interface CourseSkillResponse {
  id: number;
  title: string;
  slug: string;
}

export interface DiscountDto {
  id?: number;
  title: string;
  discountStrategy: 'PERCENT' | 'PRICE';
  type: 'COURSE' | 'USER' | 'PLATFORM' | 'NONE' | 'COUPON';
  endedAt: string;
  discountRate: number;
  discountPrice: number;
  endedAtUTC: string;
}

export interface FindCouponTargetsResponse {
  items: CouponTargetResponse[];
  nextCursor?: string;
  hasNext: boolean;
  pageSize: number;
}

export interface GetCouponIssuableResponse {
  canIssueCoupon: boolean;
  coupon: IssuableCouponResponse;
}

export interface IssuableCouponResponse {
  id: number;
  discountType: 'PERCENT' | 'PRICE';
  discountValue: number;
}

export interface FindCommonTagResponse {
  items: FindCommonTagResponseItem[];
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface FindCommonTagResponseItem {
  id: number;
  title: string;
  subTitle: string;
  slug: string;
  relatedCount: number;
}

export interface FindRecommendCommonTagResponse {
  items: FindCommonTagResponseItem[];
}

export interface LegacyChallengeBySkillResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseCardItem[];
}

export interface ChallengeStepStatusResponse {
  info: boolean;
  curriculum: boolean;
  detail: boolean;
  coverImage: boolean;
  survey: boolean;
  setting: boolean;
  autoMessage: boolean;
  faq: boolean;
  instructor: boolean;
}

export interface ChallengeScheduleLiveSessionPeriodResponse {
  id: number;
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface GetChallengeSchedulesResponse {
  applyPeriod?: Period;
  challengePeriod?: Period;
  liveSessions: ChallengeScheduleLiveSessionPeriodResponse[];
  applyStartedAtUTC?: string;
  applyEndedAtUTC?: string;
  startedAtUTC?: string;
  endedAtUTC?: string;
}

export interface CategoryDto {
  id: number;
  slug: string;
  title: string;
}

export interface GetOnlineCourseResponse {
  id: number;
  slug: string;
  thumbnailUrl: string;
  title: string;
  totalStar: number;
  reviewCount: number;
  studentCount: number;
  representativeCategory: CategoryDto;
  mainCategories: CategoryDto[];
  subCategories: CategoryDto[];
  skillTags: CategoryDto[];
}

export interface GetUnownedCompletedChallengesResponse {
  items: UnownedCompletedChallenge[];
}

export interface UnownedCompletedChallenge {
  id: number;
  title: string;
  slug: string;
  thumbnailUrl: string;
  applicantCount: number;
}

export interface GetChallengeUsedSkillTagsResponse {
  items: UsedSkillTagItem[];
}

export interface UsedSkillTagItem {
  id: number;
  slug: string;
}

export interface LegacyChallengeCourseRecommendResponse {
  attributionToken: string;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseCardItem[];
}

export interface FreeLiveChallenge {
  id: number;
  thumbnailUrl?: string;
  title: string;
  slug: string;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  liveSessionStartedAtUTC?: string;
  liveSessionEndedAtUTC?: string;
  isAlreadyApplied: boolean;
  canApply: boolean;
  instructor: FreeLiveChallengeInstructorInfo;
  representativeCategory: CategoryInfo;
  isAlreadySubscribed: boolean;
}

export interface FreeLiveChallengeInstructorInfo {
  id: number;
  slug?: string;
  name: string;
}

export interface FreeLiveChallengeResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: FreeLiveChallenge[];
}

export interface GetFeaturedChallengeItemResponse {
  id: number;
  thumbnailUrl?: string;
  title: string;
  slug: string;
  description?: string;
  instructor: GetFeaturedChallengesInstructorInfo;
  representativeCategory: CategoryInfo;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  price: number;
  regularPrice: number;
  payPrice: number;
  discountRate?: number;
  isFree: boolean;
  isDiscounting: boolean;
  hasLiveSession: boolean;
  /**
   * use applyClosedAtUTC
   */
  isApplyEnded: boolean;
  challengeStatus:
    | 'BEFORE_APPLY'
    | 'APPLYING'
    | 'AFTER_APPLY'
    | 'PROCESSING'
    | 'PARTICIPATING'
    | 'COMPLETED';
  capacity: number;
  applicantCount: number;
  displayRemainingApplicantCount?: number;
  applyClosedAt?: string;
  applyClosedAtUTC?: string;
  applicantRate: number;
}

export interface GetFeaturedChallengesInstructorInfo {
  id: number;
  name: string;
  slug?: string;
}

export interface GetFeaturedChallengesResponse {
  items: GetFeaturedChallengeItemResponse[];
}

export interface EndedChallenge {
  id: number;
  thumbnailUrl?: string;
  title: string;
  slug: string;
  description?: string;
  instructor: GetEndedChallengeInstructorInfo;
  representativeCategory: CategoryInfo;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  price: number;
  regularPrice: number;
  payPrice: number;
  discountRate?: number;
  isFree: boolean;
  isDiscounting: boolean;
  hasLiveSession: boolean;
  /**
   * use applyClosedAtUTC
   */
  isApplyEnded: boolean;
  challengeStatus:
    | 'BEFORE_APPLY'
    | 'APPLYING'
    | 'AFTER_APPLY'
    | 'PROCESSING'
    | 'PARTICIPATING'
    | 'COMPLETED';
  capacity: number;
  applicantCount: number;
  displayRemainingApplicantCount?: number;
  isAlreadySubscribed: boolean;
  applyClosedAt?: string;
  applyClosedAtUTC?: string;
  applicantRate: number;
}

export interface GetEndedChallengeCourseResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: EndedChallenge[];
}

export interface GetEndedChallengeInstructorInfo {
  id: number;
  name: string;
  slug?: string;
}

export interface CustomIconResponse {
  iconType: string;
}

export interface GetCustomIconsResponse {
  icons: CustomIconResponse[];
}

export interface ChallengeSubscriptionItem {
  id: number;
  type: 'CHALLENGE_APPLY_STARTED' | 'NEW_CHALLENGE_OPENED';
}

export interface GetChallengeNotificationSubscriptionsResponse {
  items: ChallengeSubscriptionItem[];
}

export interface ParentCategory {
  id: number;
  title: string;
  slug: string;
}

export interface SearchCategoryDto {
  id: number;
  title: string;
  slug: string;
  parent?: ParentCategory;
}

export interface SearchCategoryResponse {
  items: SearchCategoryDto[];
}

export interface FindAllCategoriesResponse {
  categories: ParentCategoryWithChildrenDto[];
}

export interface ParentCategoryWithChildrenDto {
  id: number;
  slug: string;
  title: string;
  originalTitle: string;
  icon: string;
  subCategories: ChildCategoryDto[];
}

export interface ApiResponseFindClientCartResponse {
  statusCode:
    | 'OK'
    | 'CREATED'
    | 'BAD_REQUEST'
    | 'NOT_FOUND'
    | 'INTERNAL_SERVER_ERROR'
    | 'FORBIDDEN'
    | 'CONFLICT'
    | 'UNAUTHORIZED'
    | 'PAYLOAD_TOO_LARGE'
    | 'TOO_MANY_REQUESTS';
  message: string;
  errorCode?: string;
  data: FindClientCartResponse;
}

export interface ChallengeCartResponse {
  startedAt?: string;
  endedAt?: string;
  applyStartedAt?: string;
  startedAtUTC: string;
  endedAtUTC: string;
  applyStartedAtUTC: string;
}

export interface ClientCartItemDto {
  id: number;
  courseId: number;
  slug: string;
  title: string;
  duration?: number;
  thumbnailUrl: string;
  regularPrice: number;
  paymentPrice: number;
  krwRegularPrice: number;
  krwPaymentPrice: number;
  usdRegularPrice: number;
  usdPaymentPrice: number;
  publishedAt?: string;
  updatedAt?: string;
  discount?: DiscountDto;
  isPromotion: boolean;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  studentCount: number;
  reviewCount: number;
  reviewAverage: number;
  likeCount: number;
  level?: CourseLevelResponse;
  categories: FindClientCartCourseCategoryResponse[];
  skills: CourseSkillResponse[];
  instructorId: number;
  instructorName: string;
  kdtPrice?: number;
  currency: {
    currencyCode?: string;
    displayName?: string;
    symbol?: string;
    defaultFractionDigits?: number;
    numericCode?: number;
    numericCodeAsString?: string;
  };
  challenge?: ChallengeCartResponse;
  publishedAtUTC: string;
  updatedAtUTC: string;
}

export interface FindClientCartCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parent: ParentCategoryResponse;
}

export interface FindClientCartResponse {
  items: ClientCartItemDto[];
  now: string;
  nowUTC: string;
}

export interface BuyerInfoResponse {
  email?: string;
  phoneNumber?: string;
  name?: string;
  countryCode?: string;
  useGlobalCard: boolean;
  country: string;
}

export interface AdpickCourse {
  goodsNo: string;
  goodsNm: string;
  goodsPriceOrg: number;
  goodsPrice: number;
  link: string;
  imageUrl: string;
  reviewCnt: number;
  commission: number;
}

export interface AdpickCourseResponse {
  total_rows: number;
  current_page: number;
  list: AdpickCourse[];
}

export interface MarketingDisagreedCourse {
  p_cd: string;
  commission: number;
}

export interface CourseDetailDiscountResponse {
  title: string;
  discountedPrice: number;
  startedAt: string;
  endedAt: string;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface GetCourseDetailCourseCategoryResponse {
  id: number;
  title: string;
  parent: ParentCategoryResponse;
}

export interface GetCourseDetailInstructorResponse {
  id: number;
  name: string;
  slug?: string;
  isRepresentative: boolean;
}

export interface GetCourseDetailResponse {
  id: number;
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  type: 'ON' | 'OFF' | 'CHALLENGE';
  courseType: 'ONLINE' | 'CLASS' | 'CONFERENCE' | 'CHALLENGE';
  title: string;
  slug: string;
  thumbnailUrl: string;
  textBody: string;
  abilities: string[];
  allowedUsageAi: boolean;
  description?: string;
  keywords?: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  publishedAt?: string;
  exposure: boolean;
  courseTags: CourseTagResponse[];
  price: number;
  regularPrice: number;
  discount?: CourseDetailDiscountResponse;
  revenueRatio: number;
  totalSequence: number;
  popularSequence: number;
  likeCount: number;
  isPromotion: boolean;
  isB2bSubscription: boolean;
  categories: GetCourseDetailCourseCategoryResponse[];
  skills: CourseSkillResponse[];
  levels: CourseLevelResponse[];
  ratings: RatingResponse;
  mainInstructors: GetCourseDetailInstructorResponse[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  publishedAtUTC?: string;
  createdAtUTC: string;
  updatedAtUTC: string;
  deletedAtUTC?: string;
}

export interface RatingHistogramResponse {
  one: number;
  two: number;
  three: number;
  four: number;
  five: number;
}

export interface RatingResponse {
  totalCount: number;
  average: number;
  histogram: RatingHistogramResponse;
}

export interface CartItemCourseResponse {
  id: number;
  title: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  price: number;
  regularPrice: number;
}

export interface CartItemDiscountResponse {
  isDiscounting: boolean;
  discountedPrice: number;
  regularPrice: number;
  startedAt?: string;
  endedAt?: string;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface CartItemResponse {
  course: CartItemCourseResponse;
  discount?: CartItemDiscountResponse;
}

export interface FindCartItemsResponse {
  items: CartItemResponse[];
}

export interface GetVoucherResponse {
  id: number;
  groupId?: number;
  progress: string;
  createdAt: string;
  endedAt?: string;
}

export interface CompleteUnit {
  id: number;
  title: string;
  hasVideo: boolean;
  vodId?: string;
}

export interface GetRecentLearningsResponse {
  user: UserInfo;
  recentLearnings: RecentLearningItem[];
}

export interface RecentLearningItem {
  progress: string;
  accessedAt?: string;
  accessedAtUTC?: string;
  courseId: number;
  courseTitle: string;
  childCategories: string[];
  lectureCount: number;
  completeCount: number;
  completeUnits: CompleteUnit[];
}

export interface UserInfo {
  id: number;
  name: string;
}

export interface VoucherCourseResponse {
  id: number;
  courseId: number;
  unitId?: number;
  userId: number;
  groupCode?: string;
  progress: string;
  completeCount: number;
  limitedAt?: string;
  createdAt: string;
  accessedAt?: string;
  startedAt?: string;
  completedAt?: string;
  payPrice: number;
  courseTitle: string;
  courseDescription?: string;
  courseDuration?: number;
  regularPrice: number;
  limitedAtUTC?: string;
  createdAtUTC: string;
  accessedAtUTC?: string;
  startedAtUTC?: string;
  completedAtUTC?: string;
}

export interface InternalCourseChallengeResponse {
  page?: PageCourseChallengeItemResponse;
  instructorCount: number;
  adminCount: number;
  allCount: number;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: CourseChallengeItemResponse[];
}

export interface CompletedVoucher {
  id: number;
  courseId: number;
}

export interface FindCompletedVoucherResponse {
  vouchers: CompletedVoucher[];
}

export interface UnitTranslationItemResponse {
  id: number;
  courseId: number;
  language: 'KO' | 'EN' | 'JA' | 'VI';
  title: string;
  body: string;
}

export interface UnitTranslationResponse {
  items: UnitTranslationItemResponse[];
}

export interface GetUserOfUnansweredSurveyResponse {
  items: UserOfUnansweredSurveyDto[];
}

export interface UserOfUnansweredSurveyDto {
  userId: number;
  courseId: number;
  title: string;
  createdAt: string;
}

export interface FindRemainSettlementAmountResponse {
  amount: number;
}

export interface CourseQuizByAdminQuestion {
  id: number;
  title: string;
  explanation: string;
  answerContent: string;
  sequence: number;
}

export interface CourseQuizByAdminResponse {
  courseId: number;
  courseTitle: string;
  mainInstructorName?: string;
  quizzes: QuizItem[];
}

export interface QuizItem {
  id: number;
  quizLectureTitle: string;
  quizQuestions: CourseQuizByAdminQuestion[];
}

export interface QuizFindByAdminItem {
  id: number;
  courseId?: number;
  quizLectureId?: number;
  mainInstructorUserId?: number;
  quizLectureTitle?: string;
  createdAt: string;
}

export interface QuizFindByAdminResponse {
  items: QuizFindByAdminItem[];
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface QuizQuestionFindByAdminChoice {
  id: number;
  content: string;
  isAnswer: boolean;
  sequence: number;
}

export interface QuizQuestionFindByAdminQuestion {
  id: number;
  sequence: number;
  choices: QuizQuestionFindByAdminChoice[];
  title: string;
  explanation: string;
}

export interface QuizQuestionFindByAdminResponse {
  quizQuestions: QuizQuestionFindByAdminQuestion[];
  courseTitle?: string;
  quizLectureTitle?: string;
  quizLectureId?: number;
}

export interface GetOnePromotionCardResponse {
  id: number;
  title: string;
  type: 'COURSE' | 'URL';
  course?: PromotionCardCourse;
  url?: string;
  tagTitle: string;
  sequence: number;
  imageId?: number;
  imageUrl?: string;
  bannerImageId?: number;
  bannerImageUrl?: string;
  bannerMobileImageId?: number;
  bannerMobileImageUrl?: string;
  bannerTitle?: string;
  isDDayActive: boolean;
  altText?: string;
  activeStatus: 'ACTIVE' | 'INACTIVE';
  startedAt: string;
  endedAt?: string;
  categoryIds: number[];
  keywords: string[];
  isExposeAllCategory: boolean;
  isExposeAll: boolean;
  isExpose: boolean;
  startedAtUTC: string;
  endedAtUTC?: string;
}

export interface GetPromotionCardListResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: GetOnePromotionCardResponse[];
}

export interface PromotionCardCourse {
  id: number;
  title: string;
  slug: string;
}

export interface FindActivePlatformDiscountResponse {
  discount?: PlatformDiscountResponse;
}

export interface PlatformDiscountResponse {
  id: number;
  title: string;
  value: number;
  startedAtUTC: string;
  endedAtUTC: string;
}

export interface FindOrderItemResponse {
  id: number;
  regularPrice: number;
  paymentPrice: number;
  createdAt: string;
  title: string;
}

export interface GetMyRoadmapResponse {
  id: number;
  status: 'DRAFT' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PRIVATE';
  title: string;
  thumbnail?: Thumbnail;
  jsonDescription: string;
  htmlDescription: string;
  courses: RoadmapCourseDto[];
  latestPublishedHistory?: PublishedHistory;
}

export interface RoadmapCourseDto {
  id: number;
  title: string;
  thumbnailUrl: string;
  defaultDescription: string;
  instructorName: string;
  description?: string;
}

export interface FindMissionByAdminItem {
  missionId: number;
  missionTitle: string;
  courseTitle: string;
  courseId: number;
  courseSlug: string;
  unitId: number;
  instructorUserId: number;
  attachmentType: 'LINK' | 'IMAGE' | 'FILE' | 'NONE';
  link?: string;
  imageFileId?: number;
  attachmentFileId?: number;
}

export interface FindMissionByAdminResponse {
  items: FindMissionByAdminItem[];
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface MentoringOrderResponse {
  id: number;
  title: string;
  regularPrice: number;
  paymentPrice: number;
  status:
    | 'CANCEL_DELETE'
    | 'MENTEE_CANCEL'
    | 'PARTIAL_CANCEL'
    | 'CANCEL_NOT_ENOUGH_PROGRESS'
    | 'GROUP_PAYMENT_CANCEL'
    | 'PAYMENT_CANCEL'
    | 'ORDER_FORM'
    | 'CONFLICT_CANCEL'
    | 'AUTO_CANCEL'
    | 'PAYMENT_READY'
    | 'TOTAL_CANCEL'
    | 'PAYMENT_FAIL'
    | 'PAYMENT_STOP'
    | 'CODE_ORDER'
    | 'ORDER_CANCEL'
    | 'PAYMENT_COMPLETE'
    | 'PAYMENT_COMPLETE_PROCESSING'
    | 'ADMIN_CANCEL'
    | 'MENTOR_CANCEL'
    | 'DEPOSIT_WAIT'
    | 'WC_COMPLETED'
    | 'MANUAL_DELETE'
    | 'REFUND_REQUEST';
  paymentChannel:
    | 'TOSS'
    | 'PAYMENTWALL'
    | 'TAZAPAY'
    | 'PAYPAL'
    | 'PAYLETTER'
    | 'IAMPORT'
    | 'NAVERPAY'
    | 'NONE'
    | 'TOSS_PAY';
  paymentMethod:
    | 'CARD'
    | 'NAVERPAY'
    | 'NAVERPAY_MONEY'
    | 'KAKAOPAY'
    | 'TOSSPAY'
    | 'TOSS_PAY_MONEY'
    | 'SAMSUNGPAY'
    | 'APPLEPAY'
    | 'PAYCO'
    | 'VIRTUAL_ACCOUNT'
    | 'BANK_TRANSFER'
    | 'LIFE_EDUCATION'
    | 'OVERSEAS'
    | 'INFPAY'
    | 'VBANK'
    | 'CARD_LEGACY'
    | 'NAVERPAY_LEGACY'
    | 'POINT'
    | 'PHONE'
    | 'EMPTY'
    | 'NONE'
    | 'FPX'
    | 'SOFORT'
    | 'PAYNOW'
    | 'GRABPAYPH'
    | 'GRABPAYMY'
    | 'BOOST'
    | 'TOUCHNGO'
    | 'KONBINI'
    | 'PAYSHOP'
    | 'BANCONTACT'
    | 'EPS'
    | 'MULTIBANCO'
    | 'PRZELEWY24'
    | 'BLIK'
    | 'IDEAL'
    | 'PLATFORM'
    | 'POLI'
    | 'INSTANT_BANK_TRANSFER'
    | 'UPI';
  paidAtUTC?: string;
  receiptUrl?: string;
}

export interface FindMarketingPartnerResponse {
  page?: PageMarketingPartnerItem;
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: MarketingPartnerItem[];
}

export interface MarketingPartnerItem {
  user: MarketingPartnerUser;
  marketing: MarketingPartnerMarketing;
}

export interface MarketingPartnerMarketing {
  commissionRate: CommissionRate;
  reservedCommissionRate?: ReservedCommissionRate;
  attributionWindow: number;
  items: unknown[];
  activityLinks: string[];
  type: 'BUSINESS' | 'PERSONAL';
  status: 'PENDING' | 'APPROVED';
}

export interface MarketingPartnerUser {
  id: number;
  email: string;
  nickname: string;
  realName: string;
  createdAt: string;
  accessedAt?: string;
}

export interface PageMarketingPartnerItem {
  totalElements?: number;
  totalPages?: number;
  size?: number;
  content?: MarketingPartnerItem[];
  number?: number;
  sort?: SortObject;
  first?: boolean;
  last?: boolean;
  numberOfElements?: number;
  pageable?: PageableObject;
  empty?: boolean;
}

export interface ReservedCommissionRate {
  agreed?: number;
  disagreed?: number;
  startedAt?: string;
}

export interface GetMarketingPartnerHistoriesResponse {
  items: MarketingPartnerHistoryItem[];
}

export interface MarketingPartnerHistoryItem {
  updater: Updater;
  startedAt: string;
  updatedAt: string;
  commissionRate: CommissionRate;
  attributionWindow: number;
}

export interface Updater {
  userId?: number;
  name?: string;
}

export interface CourseByUserDetailResponse {
  items: CourseDetailInfo[];
}

export interface FindInstitutionResponse {
  id: number;
  title: string;
  iconUrl?: string;
}

export interface OffsetPageResponseGetVouchersResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: GetVouchersResponse[];
}

export interface Curriculum {
  sectionId: number;
  title: string;
  units: Unit[];
}

export interface FindLearningStatusResponse {
  courseId: number;
  courseTitle: string;
  curriculum: Curriculum[];
}

export interface Unit {
  id: number;
  title: string;
  runtime: number;
  completedAt?: string;
}

export interface GetCourseItemResponse {
  id: number;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  title: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  based: string[];
  abilities: string[];
  targets: string[];
  description: string;
  exposure: boolean;
  runtime: number;
  totalStar: number;
  reviewCnt: number;
  studentCnt: number;
  slug: string;
  url: string;
  instructorName: string;
  thumbnailUrl: string;
  publishedAt?: string;
  lastUpdatedAt?: string;
  publishedAtUTC?: string;
  lastUpdatedAtUTC: string;
}

export interface GetCoursesResponse {
  courses: GetCourseItemResponse[];
}

export interface AdminCourseLevelResponse {
  id: number;
  name: string;
  isSelected: boolean;
}

export interface ChallengeInfo {
  startedAt?: string;
  endedAt?: string;
  applyStartedAt?: string;
  applyEndedAt?: string;
  capacity: number;
  minCapacity: number;
  startedAtUTC?: string;
  endedAtUTC?: string;
  applyStartedAtUTC?: string;
  applyEndedAtUTC?: string;
}

export interface CourseManagerInfo {
  id: number;
  courseId: number;
  manager: string;
  createdAt?: string;
  createdAtUTC: string;
}

export interface GetAdminCourseDetailInstructorResponse {
  instructorUserId: number;
  instructorUserEmail: string;
  instructorUsername: string;
  instructorType: 'MAIN' | 'SUB';
}

export interface GetAdminCourseDetailResponse {
  id: number;
  title: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  type: 'ON' | 'OFF' | 'CHALLENGE';
  slug?: string;
  enSlug?: string;
  price?: number;
  publishedAt?: string;
  lastUpdatedAt: string;
  keywords?: string;
  exposure: boolean;
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  contractStatus: 'DRAFT' | 'NOT_SIGNED' | 'CANCELED' | 'SIGNED' | 'TERMINATED' | 'NO_REQUIRED';
  revenueUserId: number;
  revenueUsername: string;
  contractAgreedAtList?: string[];
  levels: AdminCourseLevelResponse[];
  categories: GetAdminDetailCourseCategoryResponse[];
  skillTags: GetAdminCourseDetailSkillTagResponse[];
  commonTags: CommonTagResponse[];
  challengeInfo: ChallengeInfo;
  courseManagerInfo?: CourseManagerInfo;
  instructors: GetAdminCourseDetailInstructorResponse[];
  contractAgreedAtUTCList?: string[];
}

export interface GetAdminCourseDetailSkillTagResponse {
  id: number;
  name: string;
}

export interface GetAdminDetailCourseCategoryResponse {
  id: number;
  title: string;
  slug: string;
  parentTitle: string;
  parentSlug: string;
}

export interface GetTranslatedCourseDetailResponse {
  requestLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  responseLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  title: string;
  level: string;
  levelCode: string;
  description: string;
  shownCategory: string[];
  abilities: string[];
  targets: string[];
  based: string[];
  mainInstructors: MainInstructorResponse[];
  publishedAt?: string;
  lastUpdatedAt: string;
  curriculum: Section[];
  lastUpdatedAtUTC: string;
  publishedAtUTC?: string;
}

export interface Section {
  id: number;
  title: string;
  units: Unit[];
}

export interface CourseSummaryDto {
  id: number;
  title: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  slug: string;
  originalLanguage: 'KO' | 'EN' | 'JA' | 'VI';
  vodOriginalLanguage:
    | 'KO'
    | 'EN'
    | 'JA'
    | 'VI'
    | 'CN'
    | 'TW'
    | 'HI'
    | 'FR'
    | 'CA'
    | 'ES'
    | 'MX'
    | 'PT';
  thumbnailUrl: string;
  type: 'ON' | 'OFF' | 'CHALLENGE';
  instructor: Instructor;
}

export interface CourseSummaryResponse {
  items: CourseSummaryDto[];
}

export interface Instructor {
  userId: number;
  name: string;
}

export interface CourseStudentCountDto {
  id: number;
  studentCount: number;
}

export interface GetCourseStudentResponse {
  items: CourseStudentCountDto[];
}

export interface CourseTranslationListResponse {
  items: CourseTranslationResponse[];
}

export interface CourseTranslationResponse {
  courseId: number;
  title: string;
  description: string;
  language: 'KO' | 'EN' | 'JA' | 'VI';
}

export interface MultipleCourseTranslationListResponse {
  items: MultipleCourseTranslationResponse[];
}

export interface MultipleCourseTranslationResponse {
  courseId: number;
  title: string;
  description: string;
  language: 'KO' | 'EN' | 'JA' | 'VI';
  htmlBody: string;
  abilities: string[];
}

export interface FindAnswerableCourseItem {
  id: number;
  canAnswer: boolean;
}

export interface FindAnswerableCourseSurveyResponse {
  items: FindAnswerableCourseItem[];
}

export interface FindCourseSurveyAnswerResponse {
  id: number;
  createdAt: string;
  userId: number;
  userName: string;
  questionId?: number;
  content?: string;
  answeredAt?: string;
}

export interface CourseProgressRateDto {
  courseId: number;
  progressRateHistogram: ProgressRateHistogramItem[];
}

export interface GetCourseProgressRateResponse {
  items: CourseProgressRateDto[];
}

export interface ProgressRateHistogramItem {
  percent: number;
  count: number;
}

export interface CourseCrossStudentItem {
  instructorId: number;
  title: string;
  students: number;
}

export interface GetCourseCrossStudentResponse {
  items: CourseCrossStudentItem[];
}

export interface CourseCompleteRateItem {
  id: number;
  title: string;
  percent: number;
}

export interface GetCourseCompleteRateResponse {
  items: CourseCompleteRateItem[];
}

export interface CourseSkillAggregateResult {
  count: number;
  completedAt?: string;
  certificatedAt?: string;
  startedAt: string;
  createdAt?: string;
  isCompleted: boolean;
}

export interface FindCourseContractHistoryResponse {
  ids: number[];
}

export interface CourseGiftByAdminItem {
  id: number;
  orderId: number;
  status: 'READY_TO_RECEIVE' | 'RECEIVED' | 'EXPIRED' | 'CANCELED';
  message?: string;
  code: string;
  createdAt: string;
  canceledAt?: string;
  approvedAt?: string;
  senderUser: SenderUserInfo;
  receiverUser: ReceiverUserInfo;
  createdAtUTC: string;
  canceledAtUTC?: string;
  approvedAtUTC?: string;
}

export interface CourseGiftByAdminResponse {
  items: CourseGiftByAdminItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPage: number;
}

export interface ReceiverUserInfo {
  id?: number;
  name: string;
}

export interface SenderUserInfo {
  id: number;
  name: string;
}

export interface CourseDiscountResponse {
  discountTitle?: string;
  coursePrice: CoursePriceResponse;
  usdCoursePrice: CoursePriceResponse;
  discountStartedAt?: string;
  discountEndedAt?: string;
  discountStartedAtUTC?: string;
  discountEndedAtUTC?: string;
}

export interface CoursePriceResponse {
  price: number;
  discountedPrice: number;
  regularPrice: number;
  sellingPrice: number;
  discountRate: number;
}

export interface GetCourseCoverAiGenerationTemplateInputResponse {
  prompts: PromptItem[];
  referenceImages: string[];
}

export interface CourseCoverResponse {
  vodId?: string;
}

export interface GetCourseContentAiGenerationInputsResponse {
  course: CourseResponse;
  representativeInstructor: RepresentativeInstructorResponse;
  cover: CourseCoverResponse;
  based: string[];
  abilities: string[];
  targets: string[];
  description?: string;
}

export interface RepresentativeInstructorResponse {
  name: string;
  introduction?: string;
}

export interface GetGroupUserCertificateDetailResponse {
  userId: number;
  courseId: number;
  voucherId: number;
  userRealName?: string;
  groupUserName?: string;
  courseTitle: string;
  courseRuntime: number;
  instructorName: string;
  startedAtUTC: string;
  certificatedAtUTC: string;
}

export interface CertificateSummaryDto {
  voucherId: number;
  courseId: number;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  certificatedAt?: string;
  title: string;
  startedAtUTC?: string;
  completedAtUTC?: string;
  createdAtUTC: string;
  certificatedAtUTC?: string;
}

export interface GetCertificateSummaryByUserResponse {
  items: CertificateSummaryDto[];
}

export interface FindCoteVoucherResponse {
  id?: number;
}

export interface ChallengeCategory {
  isMainCategory: boolean;
  name: string;
}

export interface OriginalCourse {
  id: number;
  name: string;
  slug: string;
}

export interface SearchChallengeInstructorInfo {
  userId: number;
  name: string;
}

export interface SearchChallengeResponse {
  items: SearchedChallenge[];
  totalCount: number;
}

export interface SearchedChallenge {
  id: number;
  title: string;
  slug: string;
  status: 'DRAFT' | 'PRIVATE' | 'SUBMIT' | 'PUBLISH' | 'PAUSE' | 'PUBLISH_READY';
  price?: number;
  publishedAt?: string;
  contractedAt?: string;
  instructor: SearchChallengeInstructorInfo;
  categories: ChallengeCategory[];
  originalCourse?: OriginalCourse;
  isOnDiscount: boolean;
  publishedAtUTC?: string;
  contractedAtUTC?: string;
}

export interface AdminFeaturedChallengeInstructorInfo {
  id: number;
  slug?: string;
  name: string;
}

export interface AdminFeaturedChallengeItemResponse {
  id: number;
  thumbnailUrl?: string;
  title: string;
  description?: string;
  instructor: AdminFeaturedChallengeInstructorInfo;
  representativeCategory: CategoryInfo;
  applyStartedAtUTC: string;
  applyEndedAtUTC: string;
  exposureStartedAtUTC?: string;
  exposureEndedAtUTC?: string;
  isExposed: boolean;
  price: number;
  regularPrice: number;
}

export interface AdminFeaturedChallengeResponse {
  items: AdminFeaturedChallengeItemResponse[];
}

export interface CartDto {
  id: number;
  createdAt: string;
  updatedAt: string;
  type: string;
  status?: string;
  quantity: number;
  title?: string;
  price?: number;
  payPrice?: number;
  deletedAt?: string;
  paidAt?: string;
  regPrice?: number;
  revenue?: number;
  byInstAt?: string;
  courseDc: boolean;
  courseDcTitle?: string;
  userId: number;
  courseId?: number;
  orderId?: number;
  instUserId?: number;
  course?: object;
  legacy?: object;
  couponUserId?: number;
  revenueInfo?: object;
  couponId?: number;
  discountId?: number;
  userDcId?: number;
  createdAtUTC: string;
  updatedAtUTC: string;
  deletedAtUTC?: string;
  paidAtUTC?: string;
  byInstAtUTC?: string;
}

export interface FindCartResponse {
  items: CartDto[];
}

export interface RemoveCourseRequest {
  listId: number;
  courseId: number;
}

export interface RemoveCartsRequest {
  courseIds: number[];
}

export const enum CourseType {
  ON = 'ON',
  OFF = 'OFF',
  CHALLENGE = 'CHALLENGE',
}

export const enum Schema {
  ON = 'ON',
  OFF = 'OFF',
  CHALLENGE = 'CHALLENGE',
}

export const enum MyVoucherStudyingType {
  STUDYING = 'STUDYING',
  COMPLETED = 'COMPLETED',
}

export const enum MyVouchersSortType {
  COURSE_TITLE = 'COURSE_TITLE',
  RECENT_STUDYING = 'RECENT_STUDYING',
  RECENT_APPLY = 'RECENT_APPLY',
  EARLIEST_EXPIRATION = 'EARLIEST_EXPIRATION',
}

export const enum Language {
  KO = 'KO',
  EN = 'EN',
  JA = 'JA',
  VI = 'VI',
}

export const enum MyVoucherChallengeStudyingType {
  STUDYING = 'STUDYING',
}

export const enum MyChallengeVouchersOffsetSortType {
  COURSE_TITLE = 'COURSE_TITLE',
  RECENT_STUDYING = 'RECENT_STUDYING',
  RECENT_APPLY = 'RECENT_APPLY',
}

export const enum SearchPartnerCourseSort {
  POPULAR = 'POPULAR',
  RECENT = 'RECENT',
  PRICE = 'PRICE',
  REVENUE = 'REVENUE',
  MARGIN = 'MARGIN',
  REVIEW = 'REVIEW',
  STUDENT = 'STUDENT',
  RATING = 'RATING',
}

export const enum GetUserProfileCourseSort {
  RECENT = 'RECENT',
  POPULAR = 'POPULAR',
  RECOMMEND = 'RECOMMEND',
}

export const enum LikedCourseSort {
  RECENT = 'RECENT',
  TITLE = 'TITLE',
  STUDENT = 'STUDENT',
  RATING = 'RATING',
}

export const enum LikedCourseChargeType {
  FREE = 'FREE',
  PAID = 'PAID',
  ALL = 'ALL',
}

export const enum CourseCertificateSortType {
  CERTIFICATED = 'CERTIFICATED',
  CONFIRMED = 'CONFIRMED',
}

/** RoadmapItemType */
export const enum RoadmapItemType {
  COURSE = 'COURSE',
  BOOK = 'BOOK',
  LINK = 'LINK',
}

/** MissionAttachmentRequestType */
export const enum MissionAttachmentRequestType {
  LINK = 'LINK',
  IMAGE = 'IMAGE',
  FILE = 'FILE',
}

/** BankCode */
export const enum BankCode {
  NH = 'NH',
  KB = 'KB',
  WOORI = 'WOORI',
  SHINHAN = 'SHINHAN',
  IBK = 'IBK',
  HANA = 'HANA',
  GYEONGNAM = 'GYEONGNAM',
  DAEGU = 'DAEGU',
  BUSAN = 'BUSAN',
  SUHYUP = 'SUHYUP',
  POST = 'POST',
  KDB = 'KDB',
  SC = 'SC',
  CITI = 'CITI',
  DGB = 'DGB',
  GWANGJU = 'GWANGJU',
  JEJU = 'JEJU',
  JEONBUK = 'JEONBUK',
  SAE_MAUL = 'SAE_MAUL',
  SHINHYEOP = 'SHINHYEOP',
  K_BANK = 'K_BANK',
  KAKAO_BANK = 'KAKAO_BANK',
}

export const enum Type {
  MAIN = 'MAIN',
  SUB = 'SUB',
}

/** SurveyQuestionType */
export const enum SurveyQuestionType {
  SUBJECTIVE = 'SUBJECTIVE',
  OBJECTIVE = 'OBJECTIVE',
  MULTIPLE_OBJECTIVE = 'MULTIPLE_OBJECTIVE',
  FILE = 'FILE',
}

export const enum StatusCode {
  OK = 'OK',
  CREATED = 'CREATED',
  BAD_REQUEST = 'BAD_REQUEST',
  NOT_FOUND = 'NOT_FOUND',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  FORBIDDEN = 'FORBIDDEN',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export const enum DiscountType {
  COURSE = 'COURSE',
  USER = 'USER',
  PLATFORM = 'PLATFORM',
  NONE = 'NONE',
  COUPON = 'COUPON',
}

/** DomesticPgType */
export const enum DomesticPgType {
  NAVERPAY = 'NAVERPAY',
  TOSS = 'TOSS',
  TOSS_PAY = 'TOSS_PAY',
}

/** OrderPaymentMethodType */
export const enum OrderPaymentMethodType {
  CARD = 'CARD',
  NAVERPAY = 'NAVERPAY',
  NAVERPAY_MONEY = 'NAVERPAY_MONEY',
  KAKAOPAY = 'KAKAOPAY',
  PAYCO = 'PAYCO',
  TOSSPAY = 'TOSSPAY',
  TOSS_PAY_MONEY = 'TOSS_PAY_MONEY',
  APPLEPAY = 'APPLEPAY',
  VIRTUAL_ACCOUNT = 'VIRTUAL_ACCOUNT',
  LIFE_EDUCATION = 'LIFE_EDUCATION',
  OVERSEAS = 'OVERSEAS',
  INFPAY = 'INFPAY',
  BANK_TRANSFER = 'BANK_TRANSFER',
  LINKPAY = 'LINKPAY',
  UNKNOWN = 'UNKNOWN',
  NONE = 'NONE',
}

/** GlobalPgType */
export const enum GlobalPgType {
  PORTONE = 'PORTONE',
  TOSS = 'TOSS',
}

/** GlobalPayMethod */
export const enum GlobalPayMethod {
  NONE = 'NONE',
  PAYPAL = 'PAYPAL',
}

/** CurrencyCode */
export const enum CurrencyCode {
  KRW = 'KRW',
  USD = 'USD',
  VND = 'VND',
  JPY = 'JPY',
}

export const enum Status {
  READY = 'READY',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_FOR_DEPOSIT = 'WAITING_FOR_DEPOSIT',
  DONE = 'DONE',
  CANCELED = 'CANCELED',
  PARTIAL_CANCELED = 'PARTIAL_CANCELED',
  ABORTED = 'ABORTED',
  EXPIRED = 'EXPIRED',
}

/** MarketingPartnerType */
export const enum MarketingPartnerType {
  BUSINESS = 'BUSINESS',
  PERSONAL = 'PERSONAL',
}

/** LiveSessionLectureRole */
export const enum LiveSessionLectureRole {
  ADMIN = 'ADMIN',
  INSTRUCTOR = 'INSTRUCTOR',
  STUDENT = 'STUDENT',
  STUDENT_VIEWER = 'STUDENT_VIEWER',
  RECORDER = 'RECORDER',
}

/** CourseGiftReceiveType */
export const enum CourseGiftReceiveType {
  PHONE = 'PHONE',
  LINK = 'LINK',
}

/** CourseContractHistoryType */
export const enum CourseContractHistoryType {
  PLATFORM = 'PLATFORM',
  INFLUENCER = 'INFLUENCER',
  OFFLINE = 'OFFLINE',
  E = 'E',
  EXCLUSIVE = 'EXCLUSIVE',
  NONEXCLUSIVE = 'NONEXCLUSIVE',
}

/** EarlyBirdSaleTerm */
export const enum EarlyBirdSaleTerm {
  AGREE = 'AGREE',
  DISAGREE = 'DISAGREE',
  DELEGATE = 'DELEGATE',
}

export const enum TargetType {
  ALL = 'ALL',
  SELECTED = 'SELECTED',
  CATEGORY = 'CATEGORY',
  SKILL = 'SKILL',
  ROADMAP = 'ROADMAP',
  COMMON_TAG = 'COMMON_TAG',
}

export const enum CancelType {
  B2B = 'B2B',
  B2C = 'B2C',
}

export const enum DiscountStrategy {
  PERCENT = 'PERCENT',
  PRICE = 'PRICE',
}

/** CourseStatus */
export const enum CourseStatus {
  DRAFT = 'DRAFT',
  PRIVATE = 'PRIVATE',
  SUBMIT = 'SUBMIT',
  PUBLISH = 'PUBLISH',
  PAUSE = 'PAUSE',
  PUBLISH_READY = 'PUBLISH_READY',
}

/** OrderStatus */
export const enum OrderStatus {
  CANCEL_DELETE = 'CANCEL_DELETE',
  MENTEE_CANCEL = 'MENTEE_CANCEL',
  PARTIAL_CANCEL = 'PARTIAL_CANCEL',
  CANCEL_NOT_ENOUGH_PROGRESS = 'CANCEL_NOT_ENOUGH_PROGRESS',
  GROUP_PAYMENT_CANCEL = 'GROUP_PAYMENT_CANCEL',
  PAYMENT_CANCEL = 'PAYMENT_CANCEL',
  ORDER_FORM = 'ORDER_FORM',
  CONFLICT_CANCEL = 'CONFLICT_CANCEL',
  AUTO_CANCEL = 'AUTO_CANCEL',
  PAYMENT_READY = 'PAYMENT_READY',
  TOTAL_CANCEL = 'TOTAL_CANCEL',
  PAYMENT_FAIL = 'PAYMENT_FAIL',
  PAYMENT_STOP = 'PAYMENT_STOP',
  CODE_ORDER = 'CODE_ORDER',
  ORDER_CANCEL = 'ORDER_CANCEL',
  PAYMENT_COMPLETE = 'PAYMENT_COMPLETE',
  PAYMENT_COMPLETE_PROCESSING = 'PAYMENT_COMPLETE_PROCESSING',
  ADMIN_CANCEL = 'ADMIN_CANCEL',
  MENTOR_CANCEL = 'MENTOR_CANCEL',
  DEPOSIT_WAIT = 'DEPOSIT_WAIT',
  WC_COMPLETED = 'WC_COMPLETED',
  MANUAL_DELETE = 'MANUAL_DELETE',
  REFUND_REQUEST = 'REFUND_REQUEST',
}

export const enum PaymentMethodType {
  CARD = 'CARD',
  NAVERPAY = 'NAVERPAY',
  NAVERPAY_MONEY = 'NAVERPAY_MONEY',
  KAKAOPAY = 'KAKAOPAY',
  TOSSPAY = 'TOSSPAY',
  TOSS_PAY_MONEY = 'TOSS_PAY_MONEY',
  APPLEPAY = 'APPLEPAY',
  VIRTUAL_ACCOUNT = 'VIRTUAL_ACCOUNT',
  LIFE_EDUCATION = 'LIFE_EDUCATION',
  OVERSEAS = 'OVERSEAS',
  INFPAY = 'INFPAY',
  BANK_TRANSFER = 'BANK_TRANSFER',
  PAYCO = 'PAYCO',
  UNKNOWN = 'UNKNOWN',
  NONE = 'NONE',
  LINKPAY = 'LINKPAY',
  LINKPAY_NAVERPAY = 'LINKPAY_NAVERPAY',
  LINKPAY_KAKAOPAY = 'LINKPAY_KAKAOPAY',
  LINKPAY_TOSSPAY = 'LINKPAY_TOSSPAY',
  LINKPAY_PAYCO = 'LINKPAY_PAYCO',
  LINKPAY_APPLEPAY = 'LINKPAY_APPLEPAY',
  LINKPAY_CARD = 'LINKPAY_CARD',
  LINKPAY_BANK_TRANSFER = 'LINKPAY_BANK_TRANSFER',
  FPX = 'FPX',
  SOFORT = 'SOFORT',
  PAYNOW = 'PAYNOW',
  GRABPAYPH = 'GRABPAYPH',
  GRABPAYMY = 'GRABPAYMY',
  BOOST = 'BOOST',
  TOUCHNGO = 'TOUCHNGO',
  KONBINI = 'KONBINI',
  PAYSHOP = 'PAYSHOP',
  BANCONTACT = 'BANCONTACT',
  EPS = 'EPS',
  MULTIBANCO = 'MULTIBANCO',
  PRZELEWY24 = 'PRZELEWY24',
  BLIK = 'BLIK',
  IDEAL = 'IDEAL',
  PLATFORM = 'PLATFORM',
  POLI = 'POLI',
  INSTANT_BANK_TRANSFER = 'INSTANT_BANK_TRANSFER',
  UPI = 'UPI',
}

export const enum PaymentChannel {
  TOSS = 'TOSS',
  PAYMENTWALL = 'PAYMENTWALL',
  TAZAPAY = 'TAZAPAY',
  PAYPAL = 'PAYPAL',
  PAYLETTER = 'PAYLETTER',
  IAMPORT = 'IAMPORT',
  NAVERPAY = 'NAVERPAY',
  NONE = 'NONE',
  TOSS_PAY = 'TOSS_PAY',
}

export const enum RefundStatus {
  IDLE = 'IDLE',
  PENDING = 'PENDING',
  COMPLETE = 'COMPLETE',
  REJECT = 'REJECT',
}

/** RoadmapStatus */
export const enum RoadmapStatus {
  DRAFT = 'DRAFT',
  SUBMIT = 'SUBMIT',
  PUBLISH = 'PUBLISH',
  PAUSE = 'PAUSE',
  PRIVATE = 'PRIVATE',
}

/** RoadmapHistoryType */
export const enum RoadmapHistoryType {
  RESTORE = 'RESTORE',
  PUBLISH = 'PUBLISH',
  DRAFT = 'DRAFT',
}

export const enum VodOriginalLanguage {
  KO = 'KO',
  EN = 'EN',
  JA = 'JA',
  VI = 'VI',
  CN = 'CN',
  TW = 'TW',
  HI = 'HI',
  FR = 'FR',
  CA = 'CA',
  ES = 'ES',
  MX = 'MX',
  PT = 'PT',
}

/** CourseSubType */
export const enum CourseSubType {
  ONLINE = 'ONLINE',
  CLASS = 'CLASS',
  CONFERENCE = 'CONFERENCE',
  CHALLENGE = 'CHALLENGE',
}

/** SubmissionStatusResponse */
export const enum SubmissionStatusResponse {
  NONE = 'NONE',
  DRAFT = 'DRAFT',
  REJECTED = 'REJECTED',
  SUBMITTED = 'SUBMITTED',
  ACCEPTED = 'ACCEPTED',
}

/** LiveSessionAttendanceStatus */
export const enum LiveSessionAttendanceStatus {
  NONE = 'NONE',
  ATTENDED = 'ATTENDED',
  ABSENT = 'ABSENT',
}

export const enum RecordingOption {
  PROVIDED = 'PROVIDED',
  NOT_PROVIDED = 'NOT_PROVIDED',
}

/** CourseContentType */
export const enum CourseContentType {
  EDITOR = 'EDITOR',
  BUILDER = 'BUILDER',
}

/** CoursePaymentInfoType */
export const enum CoursePaymentInfoType {
  ENROLLED = 'ENROLLED',
  PURCHASE_UNAVAILABLE = 'PURCHASE_UNAVAILABLE',
  B2B_SUBSCRIPTION = 'B2B_SUBSCRIPTION',
  B2B_ENROLMENT_PENDING = 'B2B_ENROLMENT_PENDING',
  B2B_PURCHASE = 'B2B_PURCHASE',
  B2B_PURCHASE_RESTRICTED = 'B2B_PURCHASE_RESTRICTED',
  FREE = 'FREE',
  DISCOUNTED_PURCHASE = 'DISCOUNTED_PURCHASE',
  REGULAR_PRICE_PURCHASE = 'REGULAR_PRICE_PURCHASE',
}

export const enum ChallengeStatus {
  BEFORE_APPLY = 'BEFORE_APPLY',
  APPLYING = 'APPLYING',
  AFTER_APPLY = 'AFTER_APPLY',
  PROCESSING = 'PROCESSING',
  PARTICIPATING = 'PARTICIPATING',
  COMPLETED = 'COMPLETED',
}

/** OfflineCourseType */
export const enum OfflineCourseType {
  CONFERENCE = 'CONFERENCE',
  CLASS = 'CLASS',
}

export const enum Tag {
  IS_ACTIVE_IN_QUESTION = 'IS_ACTIVE_IN_QUESTION',
  IS_B2B_SUBSCRIPTION = 'IS_B2B_SUBSCRIPTION',
  HAS_MISSION = 'HAS_MISSION',
  IS_CHALLENGE_APPLYING = 'IS_CHALLENGE_APPLYING',
  IS_CHALLENGE_BEFORE_APPLY = 'IS_CHALLENGE_BEFORE_APPLY',
  IS_CHALLENGE_AFTER_APPLY = 'IS_CHALLENGE_AFTER_APPLY',
  IS_CHALLENGE_PROCESSING = 'IS_CHALLENGE_PROCESSING',
  IS_CHALLENGE_COMPLETED = 'IS_CHALLENGE_COMPLETED',
  IS_CHALLENGE_PARTICIPATING = 'IS_CHALLENGE_PARTICIPATING',
}

export const enum UnitType {
  SECTION = 'SECTION',
  LECTURE = 'LECTURE',
  MISSION = 'MISSION',
  DOCUMENT = 'DOCUMENT',
  QUIZ = 'QUIZ',
  LIVE_SESSION = 'LIVE_SESSION',
  LABLUP = 'LABLUP',
}

export const enum SubmissionStatus {
  DRAFT = 'DRAFT',
  REJECTED = 'REJECTED',
  SUBMITTED = 'SUBMITTED',
  ACCEPTED = 'ACCEPTED',
}

export const enum ProductType {
  ONLINE_COURSE = 'ONLINE_COURSE',
  OFFLINE_COURSE = 'OFFLINE_COURSE',
  MENTORING = 'MENTORING',
}

/** UnitContentSummaryType */
export const enum UnitContentSummaryType {
  VOD_SUBTITLE = 'VOD_SUBTITLE',
  UNIT_BODY = 'UNIT_BODY',
}

/** QuizQuestionReactionType */
export const enum QuizQuestionReactionType {
  LIKE = 'LIKE',
  DISLIKE = 'DISLIKE',
}

/** RoadmapContentTemplateAuthorType */
export const enum RoadmapContentTemplateAuthorType {
  INFLAB = 'INFLAB',
  USER = 'USER',
}

export const enum Rank {
  MASTER = 'MASTER',
  SCHOLAR = 'SCHOLAR',
  SKILL_BUILDER = 'SKILL_BUILDER',
  LEARNER = 'LEARNER',
  STARTER = 'STARTER',
}

export const enum MaxStudyingTimeDayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export const enum MaxStudyingTimeTimeOfDay {
  EARLY_MORNING = 'EARLY_MORNING',
  MORNING = 'MORNING',
  AFTERNOON = 'AFTERNOON',
  EVENING = 'EVENING',
  NIGHT = 'NIGHT',
  DAWN = 'DAWN',
}

export const enum QuizUserStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  RETRYING = 'RETRYING',
  COMPLETED = 'COMPLETED',
}

export const enum QuizQuestionUserStatus {
  PASS = 'PASS',
  FAIL = 'FAIL',
  NOT_ATTEMPTED = 'NOT_ATTEMPTED',
}

/** QuizUserStatusResponse */
export const enum QuizUserStatusResponse {
  NOT_STARTED = 'NOT_STARTED',
  NO_PERMISSION = 'NO_PERMISSION',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

/** PromotionCardContentType */
export const enum PromotionCardContentType {
  COURSE = 'COURSE',
  URL = 'URL',
}

export const enum OrderMode {
  NORMAL = 'NORMAL',
  B2B_CODE = 'B2B_CODE',
  B2C_CODE = 'B2C_CODE',
  CODE = 'CODE',
  MENTOR = 'MENTOR',
  MENTORING = 'MENTORING',
  COURSE_GIFT = 'COURSE_GIFT',
  CLIP = 'CLIP',
}

/** LiveSessionType */
export const enum LiveSessionType {
  STANDARD = 'STANDARD',
  LARGE_SCALE = 'LARGE_SCALE',
}

/** CourseContractStatus */
export const enum CourseContractStatus {
  DRAFT = 'DRAFT',
  NOT_SIGNED = 'NOT_SIGNED',
  CANCELED = 'CANCELED',
  SIGNED = 'SIGNED',
  TERMINATED = 'TERMINATED',
  NO_REQUIRED = 'NO_REQUIRED',
}

export const enum DiscountStatus {
  CAN_DISCOUNT = 'CAN_DISCOUNT',
  ON_DISCOUNT = 'ON_DISCOUNT',
  COOL_TIME = 'COOL_TIME',
  UNAVAILABLE = 'UNAVAILABLE',
}

/** MissionAttachmentType */
export const enum MissionAttachmentType {
  NONE = 'NONE',
  LINK = 'LINK',
  IMAGE_FILE = 'IMAGE_FILE',
  ATTACHMENT_FILE = 'ATTACHMENT_FILE',
}

export const enum LocationType {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

/** CourseGiftStatus */
export const enum CourseGiftStatus {
  READY_TO_RECEIVE = 'READY_TO_RECEIVE',
  RECEIVED = 'RECEIVED',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
}

export const enum PayMethod {
  CARD = 'CARD',
  NAVERPAY = 'NAVERPAY',
  NAVERPAY_MONEY = 'NAVERPAY_MONEY',
  KAKAOPAY = 'KAKAOPAY',
  TOSSPAY = 'TOSSPAY',
  TOSS_PAY_MONEY = 'TOSS_PAY_MONEY',
  SAMSUNGPAY = 'SAMSUNGPAY',
  APPLEPAY = 'APPLEPAY',
  PAYCO = 'PAYCO',
  VIRTUAL_ACCOUNT = 'VIRTUAL_ACCOUNT',
  BANK_TRANSFER = 'BANK_TRANSFER',
  LIFE_EDUCATION = 'LIFE_EDUCATION',
  OVERSEAS = 'OVERSEAS',
  INFPAY = 'INFPAY',
  VBANK = 'VBANK',
  CARD_LEGACY = 'CARD_LEGACY',
  NAVERPAY_LEGACY = 'NAVERPAY_LEGACY',
  POINT = 'POINT',
  PHONE = 'PHONE',
  EMPTY = 'EMPTY',
  NONE = 'NONE',
  FPX = 'FPX',
  SOFORT = 'SOFORT',
  PAYNOW = 'PAYNOW',
  GRABPAYPH = 'GRABPAYPH',
  GRABPAYMY = 'GRABPAYMY',
  BOOST = 'BOOST',
  TOUCHNGO = 'TOUCHNGO',
  KONBINI = 'KONBINI',
  PAYSHOP = 'PAYSHOP',
  BANCONTACT = 'BANCONTACT',
  EPS = 'EPS',
  MULTIBANCO = 'MULTIBANCO',
  PRZELEWY24 = 'PRZELEWY24',
  BLIK = 'BLIK',
  IDEAL = 'IDEAL',
  PLATFORM = 'PLATFORM',
  POLI = 'POLI',
  INSTANT_BANK_TRANSFER = 'INSTANT_BANK_TRANSFER',
  UPI = 'UPI',
}

export const enum Pg {
  KAKAOPAY = 'KAKAOPAY',
  NAVERPAY = 'NAVERPAY',
  TOSS_WIDGET = 'TOSS_WIDGET',
  TOSS = 'TOSS',
  TOSS_V2 = 'TOSS_V2',
  TOSS_GLOBAL = 'TOSS_GLOBAL',
  TOSS_PAY_LEGACY = 'TOSS_PAY_LEGACY',
  TOSS_PAY = 'TOSS_PAY',
  IMP_KAKAOPAY = 'IMP_KAKAOPAY',
  IMP_NAVERPAY = 'IMP_NAVERPAY',
  IMP_NICE = 'IMP_NICE',
  IMP_JTNET = 'IMP_JTNET',
  PORTONE = 'PORTONE',
}

export const enum RequiredImageType {
  NONE = 'NONE',
  PERSON = 'PERSON',
}

/** SettlementSubjectType */
export const enum SettlementSubjectType {
  WITHHOLDING_TAX = 'WITHHOLDING_TAX',
  NON_WITHHOLDING_TAX = 'NON_WITHHOLDING_TAX',
  BUSINESS = 'BUSINESS',
}

export const enum TermType {
  NEW_CONTRACT = 'NEW_CONTRACT',
  MODIFICATION_CONTRACT = 'MODIFICATION_CONTRACT',
}

/** CourseContentTemplateAuthorType */
export const enum CourseContentTemplateAuthorType {
  INFLAB = 'INFLAB',
  INSTRUCTOR = 'INSTRUCTOR',
}

export const enum RequiredInfoType {
  NONE = 'NONE',
  INSTRUCTOR_PROFILE = 'INSTRUCTOR_PROFILE',
}

export const enum TemplateKey {
  PROBLEM = 'PROBLEM',
  RESULT = 'RESULT',
  STORY = 'STORY',
}

/** ChallengeNotificationSubscriptionType */
export const enum ChallengeNotificationSubscriptionType {
  CHALLENGE_APPLY_STARTED = 'CHALLENGE_APPLY_STARTED',
  NEW_CHALLENGE_OPENED = 'NEW_CHALLENGE_OPENED',
}

/** PromotionCardActiveStatus */
export const enum PromotionCardActiveStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const enum AttachmentType {
  LINK = 'LINK',
  IMAGE = 'IMAGE',
  FILE = 'FILE',
  NONE = 'NONE',
}

export const enum PaymentMethod {
  CARD = 'CARD',
  NAVERPAY = 'NAVERPAY',
  NAVERPAY_MONEY = 'NAVERPAY_MONEY',
  KAKAOPAY = 'KAKAOPAY',
  TOSSPAY = 'TOSSPAY',
  TOSS_PAY_MONEY = 'TOSS_PAY_MONEY',
  SAMSUNGPAY = 'SAMSUNGPAY',
  APPLEPAY = 'APPLEPAY',
  PAYCO = 'PAYCO',
  VIRTUAL_ACCOUNT = 'VIRTUAL_ACCOUNT',
  BANK_TRANSFER = 'BANK_TRANSFER',
  LIFE_EDUCATION = 'LIFE_EDUCATION',
  OVERSEAS = 'OVERSEAS',
  INFPAY = 'INFPAY',
  VBANK = 'VBANK',
  CARD_LEGACY = 'CARD_LEGACY',
  NAVERPAY_LEGACY = 'NAVERPAY_LEGACY',
  POINT = 'POINT',
  PHONE = 'PHONE',
  EMPTY = 'EMPTY',
  NONE = 'NONE',
  FPX = 'FPX',
  SOFORT = 'SOFORT',
  PAYNOW = 'PAYNOW',
  GRABPAYPH = 'GRABPAYPH',
  GRABPAYMY = 'GRABPAYMY',
  BOOST = 'BOOST',
  TOUCHNGO = 'TOUCHNGO',
  KONBINI = 'KONBINI',
  PAYSHOP = 'PAYSHOP',
  BANCONTACT = 'BANCONTACT',
  EPS = 'EPS',
  MULTIBANCO = 'MULTIBANCO',
  PRZELEWY24 = 'PRZELEWY24',
  BLIK = 'BLIK',
  IDEAL = 'IDEAL',
  PLATFORM = 'PLATFORM',
  POLI = 'POLI',
  INSTANT_BANK_TRANSFER = 'INSTANT_BANK_TRANSFER',
  UPI = 'UPI',
}

/** MarketingPartnerStatus */
export const enum MarketingPartnerStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
}

export const enum InstructorType {
  MAIN = 'MAIN',
  SUB = 'SUB',
}
